/**
 * The ChiCare API client.
 *
 * This file is the reason the build has no Inertia in it. It uses nothing but
 * `fetch` and a token-storage interface, so React Native imports it unchanged:
 * the web app passes a localStorage-backed store, the mobile app passes an
 * AsyncStorage-backed one, and every endpoint, error shape and idempotency rule
 * is written once.
 *
 * Nothing here knows about the DOM, `window`, cookies or CSRF. Bearer tokens
 * only (§8).
 */

import type {
  SupportDeskPoll,
  SupportDeskThread,
  SupportDeskView,
  SupportMessage,
  SupportPoll,
  SupportThreadView,
  AdminUserMeta,
  AdminUserRow,
  ApiEnvelope,
  ApiErrorBody,
  AuthSession,
  Category,
  HomeFeed,
  ShopFrontSettings,
  SponsoredRow,
  VerificationHow,
  VerificationResult,
  OtpChallenge,
  PaginationMeta,
  Pharmacy,
  Product,
  ProductSummary,
  Sector,
  SpeciesKey,
  SystemConfig,
  User,
  AvatarStyle,
} from './types';
import type {
  AdminDashboard,
  AppNotification,
  ClinicalReport,
  ClinicalReportRow,
  SellerComplaint,
  PermissionCatalogue,
  SellerComplaintRow,
  SellerComplaintSummary,
  SellerDashboard,
  StaffAccount,
  StaffRole,
} from './dashboard';
import type {
  AuditDisclosurePreview,
  AuditDisclosureRow,
  PlacementOption,
  ProhibitionPreview,
  ProhibitionRow,
  SponsorshipQuote,
  SponsorshipRate,
  SponsorshipRow,
} from './governance';
import type {
  GoodsReceipt,
  GoodsReceiptRow,
  InventoryOverview,
  Recall,
  RecallPreview,
  RecallRow,
  SectorOption,
  SellerPermissionCatalogue,
  SellerRecallRow,
  SellerStaffRow,
  StockMovementRow,
  StockRow,
  StockView,
} from './inventory';
import type {
  BlogArticle,
  BlogCard,
  BlogDesk,
  BlogIndex,
  BlogPostDetail,
  BlogPostInput,
  BlogPostSummary,
  ContentPageDetail,
  AdvertQuote,
  AdvertRate,
  AdvertRow,
  AdvertisableProduct,
  BannerInput,
  BannerRow,
  AdminInbox,
  CategoryArtRow,
  ShopFrontDestinationGroup,
  ContentPageSummary,
  DebtPaymentResult,
  PharmacyApprovalDetail,
  PharmacyApprovalMeta,
  PharmacyApprovalRow,
  MediaAsset,
  RefundClaimContext,
  RefundClaimDetail,
  RefundClaimRow,
  RefundQueueMeta,
  SellerDebtContact,
  SellerDebtRow,
  SellerPharmacy,
  SellerPharmacyMeta,
  SellerDebtStatement,
  SettingsHistoryEntry,
  SettingsPayload,
  ShelfLifeOverview,
  ShelfLifePreview,
  LicenceRenewalRow,
} from './admin';
import type {
  BuyerRefundClaim,
  FiledRefund,
  RefundEvidenceUpload,
  RefundOptions,
  RefundOutcome,
  RefundReasonOption,
} from './refunds';
import type {
  AdminCategoryOption,
  AdminProductDetail,
  ProductPhoto,
  AdminProductMeta,
  AdminProductRow,
  CatalogueLookupResult,
  CatalogueSearchRow,
  CatalogueSubmissionDetail,
  CatalogueSubmissionRow,
  ImportRow,
  ImportSummary,
  SellerSubmissionRow,
  SubmissionCandidate,
} from './catalogue';
import type {
  DeliveryBand,
  DeliveryZoneInput,
  DeliveryZoneMeta,
  DeliveryZoneRow,
} from './delivery';
import type {
  IncomingQuoteRequest,
  IncomingQuoteRequestDetail,
  QuoteLineInput,
  QuoteRequestDetail,
  QuoteRequestRow,
  SellerQuote,
  WantedItem,
  ReachablePharmacy,
  StateWithCover,
  QuoteDeliveryOption,
  QuoteDeliveryMeta,
  QuoteBoardRow,
  QuoteBoardMeta,
} from './prescriptions';
import type {
  Address,
  CartLine,
  RiderRunDetail,
  RiderRunRow,
  RiderRunsMeta,
  SellerBatchRow,
  SellerListingDetail,
  SellerListingRow,
  SellerListingsMeta,
  SellerOrderDetail,
  SellerOrderRow,
  SellerQueueMeta,
  DeliveryMethod,
  NewAddress,
  Order,
  PricedCart,
} from './orders';


// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Every failure the API can produce, in one throwable.
 *
 * §11: "errors say what happened and never apologise vaguely." The UI reads
 * `code` to decide what to render and `message` to say it, and `errors` to put
 * the red text under the right field.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fieldErrors: Record<string, string[]>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.fieldErrors = body.errors ?? {};
  }

  /** The first message for a field, ready to render under an input. */
  fieldError(field: string): string | undefined {
    return this.fieldErrors[field]?.[0];
  }

  get isValidation(): boolean {
    return this.status === 422;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** A retry might work: a flaky connection, a rate limit, our own bug. */
  get isRetryable(): boolean {
    return this.status >= 500 || this.status === 429 || this.status === 0;
  }
}

/** Thrown when the request never reached the API at all. */
export class NetworkError extends ApiError {
  constructor(message = 'We could not reach ChiCare. Check your connection.') {
    super(0, { message, errors: {}, code: 'NETWORK_UNREACHABLE' });
    this.name = 'NetworkError';
  }
}

// ---------------------------------------------------------------------------
// Token storage — the one platform-specific seam
// ---------------------------------------------------------------------------

export interface TokenStore {
  get(): string | null | Promise<string | null>;
  set(token: string | null): void | Promise<void>;
}

/** For tests, SSR, and any context without persistent storage. */
export class MemoryTokenStore implements TokenStore {
  private token: string | null = null;

  get(): string | null {
    return this.token;
  }

  set(token: string | null): void {
    this.token = token;
  }
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export interface ClientOptions {
  baseUrl: string;
  tokenStore?: TokenStore;
  /** Called whenever the API says the token is no longer good. */
  onUnauthenticated?: () => void;
  fetch?: typeof fetch;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** §8 / §9.14: required on every money-mutating endpoint. */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /**
   * Extra headers, merged in last.
   *
   * Added for the support widget's token, which identifies ONE enquiry and is
   * not an account credential. It goes in a header rather than the path so it
   * never lands in a server access log or a browser history entry.
   */
  headers?: Record<string, string>;
}

export class ChiCareClient {
  private readonly baseUrl: string;
  private readonly tokenStore: TokenStore;
  private readonly onUnauthenticated?: () => void;
  private readonly doFetch: typeof fetch;

  constructor(options: ClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, '');
    this.tokenStore = options.tokenStore ?? new MemoryTokenStore();
    this.onUnauthenticated = options.onUnauthenticated;
    this.doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  }

  /**
   * The API root, without a trailing slash.
   *
   * Exposed for the one case a method cannot serve: `navigator.sendBeacon`,
   * which needs a URL rather than a request, because a normal fetch issued
   * while the page is unloading is cancelled by the browser. Everything else
   * should go through a method on this class — reading this and hand-rolling a
   * fetch skips the auth header, the error shaping and the token refresh.
   */
  get apiBase(): string {
    return this.baseUrl;
  }

  // -- plumbing ------------------------------------------------------------

  async request<T>(path: string, options: RequestOptions = {}): Promise<ApiEnvelope<T>> {
    const url = new URL(`${this.baseUrl}${path}`);

    for (const [key, value] of Object.entries(options.query ?? {})) {
      if (value !== null && value !== undefined && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }

    const headers: Record<string, string> = {
      Accept: 'application/json',
    };

    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    const token = await this.tokenStore.get();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (options.idempotencyKey) {
      headers['Idempotency-Key'] = options.idempotencyKey;
    }

    // Last, so a caller can override anything above deliberately.
    Object.assign(headers, options.headers ?? {});

    let response: Response;

    try {
      response = await this.doFetch(url.toString(), {
        method: options.method ?? 'GET',
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        signal: options.signal,
      });
    } catch (cause) {
      // An aborted request is the caller changing their mind, not a failure.
      if (cause instanceof Error && cause.name === 'AbortError') throw cause;
      throw new NetworkError();
    }

    if (response.status === 204) {
      return { data: undefined as T };
    }

    const payload = await this.parseJson(response);

    if (!response.ok) {
      if (response.status === 401) {
        await this.tokenStore.set(null);
        this.onUnauthenticated?.();
      }

      throw new ApiError(response.status, this.asErrorBody(payload, response.status));
    }

    return payload as ApiEnvelope<T>;
  }

  private async parseJson(response: Response): Promise<unknown> {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  private asErrorBody(payload: unknown, status: number): ApiErrorBody {
    if (
      payload &&
      typeof payload === 'object' &&
      'message' in payload &&
      'code' in payload
    ) {
      return payload as ApiErrorBody;
    }

    // A gateway or proxy failed before Laravel saw the request.
    return {
      message: 'Something went wrong on our side.',
      errors: {},
      code: status >= 500 ? 'SERVER_ERROR' : 'UNEXPECTED_RESPONSE',
    };
  }

  private async get<T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) {
    return this.request<T>(path, { query, signal });
  }

  // -- authentication (§10 Phase 0) ----------------------------------------

  /**
   * Create an account.
   *
   * A name, a phone number and an email address, together — one code goes to
   * both channels and either one signs them in afterwards. This is a different
   * door from `requestLoginCode` on purpose: that one never discloses whether
   * an identifier is known, and it can only keep that property if the client
   * never has to ask.
   *
   * The reply's `sent_to` names both destinations, masked. Show it: somebody
   * hunting for a code that has gone to a mistyped address sees the mistyped
   * address while they are hunting.
   */
  async register(input: {
    name: string;
    phone: string;
    email: string;
  }): Promise<OtpChallenge> {
    const { data } = await this.request<OtpChallenge>('/auth/register', {
      method: 'POST',
      body: input,
    });

    return data;
  }

  /**
   * Ask for a sign-in code.
   *
   * `identifier` is whatever the person typed — a Nigerian mobile number or an
   * email address. It is sent raw, spaces and dashes included: the API decides
   * which of the two it is and normalises it, and doing that here as well would
   * mean two implementations of one rule drifting apart.
   *
   * The reply names the channels that actually took the code, which is not
   * necessarily the one it was asked for: give a number ChiCare already has an
   * email for, and it goes to both.
   */
  async requestLoginCode(identifier: string): Promise<OtpChallenge> {
    const { data } = await this.request<OtpChallenge>('/auth/request-code', {
      method: 'POST',
      body: { identifier },
    });

    return data;
  }

  /**
   * Verify the code and take the token.
   *
   * `name`, `phone` and `email` are the details a first-time buyer fills in on
   * the same screen. They are optional here because a returning buyer has them
   * already, and each is ignored server-side if it clashes with another
   * account — the sign-in still succeeds, and `missing` says what is outstanding.
   */
  async verifyLoginCode(input: {
    identifier: string;
    code: string;
    /** `register` completes a new account; omit it to sign an existing one in. */
    purpose?: 'login' | 'register';
    name?: string;
    phone?: string;
    email?: string;
    deviceName?: string;
  }): Promise<AuthSession> {
    const { data } = await this.request<AuthSession>('/auth/verify-code', {
      method: 'POST',
      body: {
        identifier: input.identifier,
        code: input.code,
        purpose: input.purpose,
        // Ignored by the API on a registration — the account is built from
        // what the code was actually delivered to, never from this request.
        name: input.name,
        phone: input.phone,
        email: input.email,
        device_name: input.deviceName,
      },
    });

    await this.tokenStore.set(data.token);

    return data;
  }

  /**
   * Fill in details skipped at sign-in.
   *
   * Blanks only. A contact already on the account is a channel one-time codes
   * are sent to, so the API will not repoint one from a signed-in session; the
   * reply says what is still outstanding either way.
   */
  async updateProfile(input: {
    name?: string;
    phone?: string;
    email?: string;
    /** A drawn portrait the buyer tapped. `null` goes back to initials. */
    avatar_style?: AvatarStyle | null;
  }): Promise<{ user: User; profile_complete: boolean; missing: AuthSession['missing'] }> {
    const { data } = await this.request<{
      user: User;
      profile_complete: boolean;
      missing: AuthSession['missing'];
    }>('/auth/profile', { method: 'PATCH', body: input });

    return data;
  }

  /**
   * The admin register of people.
   *
   * Read-only by design — there is no matching write method because there is
   * no matching endpoint.
   */
  async adminUsers(
    params: { q?: string; role?: string } = {},
    signal?: AbortSignal,
  ): Promise<{ data: AdminUserRow[]; meta: AdminUserMeta }> {
    const { data, meta } = await this.request<AdminUserRow[]>('/admin/users', {
      query: params,
      signal,
    });

    return { data, meta: meta as unknown as AdminUserMeta };
  }

  /**
   * Staff sign-in.
   *
   * Buyers, sellers and riders authenticate by phone OTP; admins and
   * pharmacists additionally hold a password, so an incident at 2am does not
   * depend on an SMS gateway being up.
   */
  async staffLogin(input: { email: string; password: string }): Promise<AuthSession> {
    const { data } = await this.request<AuthSession>('/auth/staff-login', {
      method: 'POST',
      body: input,
    });

    await this.tokenStore.set(data.token);

    return data;
  }

  /**
   * Forgot a console password — ask for a code.
   *
   * Resolves the same way whether or not the address is known, because the API
   * answers the same way. A screen that reported "no such account" would hand
   * anybody a way to ask which pharmacies and vets hold ChiCare accounts, so
   * there is deliberately nothing here to branch on.
   *
   * Buyers never reach this: they have no password to forget.
   */
  async forgotPassword(email: string): Promise<{ sent_to: string | null; resend_in: number }> {
    const { data } = await this.request<{ sent_to: string | null; resend_in: number }>(
      '/auth/forgot-password',
      { method: 'POST', body: { email } },
    );

    return data;
  }

  /**
   * Forgot a console password — set the new one.
   *
   * Returns no token on purpose. The caller sends the person to the sign-in
   * door, which is the only place the new password is actually proved to work.
   */
  async resetPassword(input: {
    email: string;
    code: string;
    password: string;
    password_confirmation: string;
  }): Promise<{ reset: boolean; message: string }> {
    const { data } = await this.request<{ reset: boolean; message: string }>(
      '/auth/reset-password',
      { method: 'POST', body: input },
    );

    return data;
  }

  // ── The shop front ──────────────────────────────────────────────────────
  //
  // Behind `pages.edit`, not `catalogue.edit`: swapping a picture must not
  // require the right to change drug facts.

  async adminBanners(sector?: string, signal?: AbortSignal): Promise<BannerRow[]> {
    const { data } = await this.get<BannerRow[]>('/admin/banners', { sector }, signal);

    return data;
  }

  async createBanner(input: BannerInput): Promise<BannerRow> {
    const { data } = await this.request<BannerRow>('/admin/banners', {
      method: 'POST',
      body: input,
    });

    return data;
  }

  async updateBanner(id: number, input: BannerInput): Promise<BannerRow> {
    const { data } = await this.request<BannerRow>(`/admin/banners/${id}`, {
      method: 'PATCH',
      body: input,
    });

    return data;
  }

  async deleteBanner(id: number): Promise<void> {
    await this.request(`/admin/banners/${id}`, { method: 'DELETE' });
  }

  /**
   * Set the order of one row.
   *
   * The whole arrangement, not "move this one up": two people each nudging a
   * slide can leave an order neither of them chose.
   */
  async reorderBanners(row: number, ids: number[]): Promise<void> {
    await this.request('/admin/banners/reorder', {
      method: 'POST',
      body: { row, ids },
    });
  }

  /**
   * Everything waiting for this member of staff, across every desk.
   *
   * One call rather than one per desk. The console could ask each desk for its
   * own count — they all report one — but that is seven requests every twenty
   * seconds, each behind its own permission, so a support agent's browser
   * console would fill with 403s for a feature that was working correctly.
   *
   * The server applies the permissions and returns the same shape whether
   * somebody can see one desk or all of them.
   */
  async adminInbox(signal?: AbortSignal): Promise<AdminInbox> {
    const response = await this.get<AdminInbox['groups']>('/admin/inbox', undefined, signal);
    const meta = (response.meta ?? {}) as { total?: number; overdue?: number };

    return {
      groups: response.data,
      // Defaults to 0 rather than to a sum of the groups: summing them
      // double-counts every subset desk, and a badge that over-counts is a
      // badge people stop trusting.
      total: meta.total ?? 0,
      overdue: meta.overdue ?? 0,
    };
  }

  // ── The stock room ───────────────────────────────────────────────────
  //
  // Three reads, all behind `stock.count`. Nothing here writes: stock moves
  // through receiving, selling and write-offs, every one of which goes via
  // StockLedger on the server.

  async sellerInventory(
    signal?: AbortSignal,
  ): Promise<{ overview: InventoryOverview; sector: string }> {
    // No sector argument. A business trades on one shelf and the server takes
    // it from the account — a parameter here would be a way to ask for a
    // combined view, which is the thing that must not exist.
    const response = await this.get<InventoryOverview>('/seller/inventory', undefined, signal);

    const meta = (response.meta ?? {}) as { sector?: string };

    return { overview: response.data, sector: meta.sector ?? '' };
  }

  async sellerStock(
    params: { view?: StockView; q?: string } = {},
    signal?: AbortSignal,
  ): Promise<{ rows: StockRow[]; valueAtCostKobo: number }> {
    const response = await this.get<StockRow[]>(
      '/seller/inventory/stock',
      {
        view: params.view && params.view !== 'all' ? params.view : undefined,
        q: params.q || undefined,
      },
      signal,
    );

    const meta = (response.meta ?? {}) as { value_at_cost_kobo?: number };

    return { rows: response.data, valueAtCostKobo: meta.value_at_cost_kobo ?? 0 };
  }

  async sellerStockMovements(
    params: { type?: string; days?: number } = {},
    signal?: AbortSignal,
  ): Promise<StockMovementRow[]> {
    const { data } = await this.get<StockMovementRow[]>(
      '/seller/inventory/movements',
      { type: params.type || undefined, days: params.days },
      signal,
    );

    return data;
  }

  async shopFrontCategories(signal?: AbortSignal): Promise<CategoryArtRow[]> {
    const { data } = await this.get<CategoryArtRow[]>('/admin/shopfront/categories', undefined, signal);

    return data;
  }

  /**
   * The places a tile may be pointed at: fixed app destinations, published
   * CMS pages, published articles.
   *
   * One call rather than three, and behind the shop front's own permission —
   * the CMS endpoints the console would otherwise have called need
   * `content.read` and `blog.read`, which a merchandiser has no reason to hold.
   */
  async shopFrontDestinations(signal?: AbortSignal): Promise<ShopFrontDestinationGroup[]> {
    const { data } = await this.get<ShopFrontDestinationGroup[]>(
      '/admin/shopfront/destinations',
      undefined,
      signal,
    );

    return data;
  }

  /** `null` removes the photograph and puts the drawn art back. */
  async setCategoryArt(categoryId: number, mediaId: number | null): Promise<void> {
    await this.request(`/admin/shopfront/categories/${categoryId}/art`, {
      method: 'PATCH',
      body: { image_media_id: mediaId },
    });
  }

  /**
   * Add a category to a shelf.
   *
   * The sector is chosen here and never afterwards: moving a category across
   * the wall takes every product filed under it with it.
   */
  async createCategory(input: {
    name: string;
    sector: string;
    image_media_id?: number | null;
    /** Where the tile leads. Omit for the category's own shelf. */
    link_path?: string | null;
  }): Promise<CategoryArtRow> {
    const { data } = await this.request<CategoryArtRow>('/admin/shopfront/categories', {
      method: 'POST',
      body: input,
    });

    return data;
  }

  /**
   * Delete a category outright.
   *
   * Refuses while anything is filed under it. Switching it OFF is the
   * reversible way to take a shelf down and keeps the products, their listings
   * and their history; deleting is for one created by mistake.
   */
  async deleteCategory(categoryId: number): Promise<void> {
    await this.request(`/admin/shopfront/categories/${categoryId}`, { method: 'DELETE' });
  }

  /** Rename one, or switch it on once it has a picture and some products. */
  async updateCategory(
    categoryId: number,
    /**
     * `link_path: null` CLEARS the destination and puts the tile back on its
     * own shelf. Omitting the key leaves whatever is stored alone, which is
     * what makes a rename safe.
     */
    input: { name?: string; is_active?: boolean; link_path?: string | null },
  ): Promise<CategoryArtRow> {
    const { data } = await this.request<CategoryArtRow>(
      `/admin/shopfront/categories/${categoryId}`,
      { method: 'PATCH', body: input },
    );

    return data;
  }


  // ─ Advertising ──────────────────────────────────────────
  //
  // A seller reads the rate card and buys from it; they never set it.

  async advertRates(signal?: AbortSignal): Promise<AdvertRate[]> {
    const { data } = await this.get<AdvertRate[]>('/seller/adverts/rates', undefined, signal);

    return data;
  }

  /**
   * Products this seller may advertise.
   *
   * §9.7 excludes prescription-only and controlled medicines entirely, server
   * side. A seller who is never shown one never has to be told no.
   */
  async advertisableProducts(
    sector: string,
    q?: string,
    signal?: AbortSignal,
  ): Promise<AdvertisableProduct[]> {
    const { data } = await this.get<AdvertisableProduct[]>(
      '/seller/adverts/products',
      { sector, q },
      signal,
    );

    return data;
  }

  async quoteAdvert(input: {
    kind: 'shop' | 'product';
    placement: string;
    sector: string;
    unit: string;
    units: number;
  }): Promise<AdvertQuote> {
    const { data } = await this.request<AdvertQuote>('/seller/adverts/quote', {
      method: 'POST',
      body: input,
    });

    return data;
  }

  async myAdverts(signal?: AbortSignal): Promise<AdvertRow[]> {
    const { data } = await this.get<AdvertRow[]>('/seller/adverts', undefined, signal);

    return data;
  }

  async buyAdvert(input: {
    kind: 'shop' | 'product';
    placement: string;
    sector: string;
    unit: string;
    units: number;
    product_id?: number | null;
  }): Promise<AdvertRow> {
    const { data } = await this.request<AdvertRow>('/seller/adverts', {
      method: 'POST',
      body: input,
    });

    return data;
  }

  /** Start paying. Returns a Paystack page to send the seller to. */
  async payForAdvert(reference: string): Promise<{
    reference: string;
    payment_reference: string;
    authorization_url: string | null;
  }> {
    const { data } = await this.request<{
      reference: string;
      payment_reference: string;
      authorization_url: string | null;
    }>(`/seller/adverts/${reference}/pay`, { method: 'POST' });

    return data;
  }

  /**
   * Confirm a payment and put the placement live.
   *
   * The server re-asks Paystack rather than believing the client (§8) ─ a
   * return from a payment page is a claim, not proof.
   */
  async confirmAdvert(reference: string): Promise<AdvertRow> {
    const { data } = await this.request<AdvertRow>(`/seller/adverts/${reference}/confirm`, {
      method: 'POST',
    });

    return data;
  }

  // ─ What a buyer sees ──────────────────────────────────

  /**
   * Sponsored shops or products for one placement.
   *
   * Its own call, into its own labelled block. These are never merged into a
   * price list: the offer ladder is ordered by what the buyer pays and says so.
   */
  async sponsored(
    params: { placement: string; sector?: string; kind?: 'shop' | 'product'; category_id?: number },
    signal?: AbortSignal,
  ): Promise<{ rows: SponsoredRow[]; disclosure: string }> {
    const { data, meta } = await this.get<SponsoredRow[]>('/sponsored', params, signal);

    return {
      rows: data,
      disclosure: (meta as { disclosure?: string } | undefined)?.disclosure ?? '',
    };
  }

  /** How to check a pack with NAFDAC. Public: no account needed to find out. */
  async verificationHow(signal?: AbortSignal): Promise<VerificationHow> {
    const { data } = await this.get<VerificationHow>('/verifications/how', undefined, signal);

    return data;
  }

  /** Record what NAFDAC replied. A signal, never an adjudication. */
  async recordVerification(input: {
    product_id: number;
    result: 'genuine' | 'fake' | 'no_reply';
    sub_order_reference?: string;
    code?: string;
  }): Promise<VerificationResult> {
    const { data } = await this.request<VerificationResult>('/verifications', {
      method: 'POST',
      body: input,
    });

    return data;
  }

  async me(signal?: AbortSignal): Promise<User> {
    const { data } = await this.get<User>('/auth/me', undefined, signal);

    return data;
  }

  async logout(): Promise<void> {
    try {
      await this.request('/auth/logout', { method: 'POST' });
    } finally {
      // Whatever the server said, this device is signed out.
      await this.tokenStore.set(null);
    }
  }

  /**
   * States and LGAs, for every form that asks where somewhere is.
   *
   * Its own endpoint rather than part of `/system/config`: 774 LGAs is a
   * payload nobody needs on a home screen. Public, because pharmacy
   * registration happens before anybody has an account.
   */
  async locations(
    signal?: AbortSignal,
  ): Promise<{ states: string[]; lgas: Record<string, string[]> }> {
    const { data } = await this.get<{ states: string[]; lgas: Record<string, string[]> }>(
      '/system/locations',
      undefined,
      signal,
    );

    return data;
  }

  // -- catalogue -----------------------------------------------------------

  /**
   * `sector` is omitted on the human side rather than sent as 'human'.
   *
   * Human is the server's default, so leaving it out keeps the human URLs byte
   * for byte what they were before animal health existed — same cache keys,
   * same logs, and no chance of a client that forgot the parameter behaving
   * differently from one that passed the default explicitly.
   */
  async home(state?: string, sector?: Sector, signal?: AbortSignal): Promise<HomeFeed> {
    const { data, meta } = await this.get<HomeFeed>(
      '/home',
      { state, sector: sector === 'veterinary' ? sector : undefined },
      signal,
    );

    // How the shop front behaves arrives in the meta, because it describes the
    // payload rather than being part of it. Merged here so every caller reads
    // one object, and defaulted so an older API that does not send it yet
    // renders a still, single-row shop front instead of throwing.
    const shopfront = (meta as { shopfront?: Partial<ShopFrontSettings> } | undefined)?.shopfront;

    return {
      ...data,
      shopfront: {
        banner_rows: shopfront?.banner_rows ?? 0,
        banner_interval_seconds: shopfront?.banner_interval_seconds ?? 6,
        banner_animation: shopfront?.banner_animation ?? 'slide',
        banner_ratio: shopfront?.banner_ratio ?? '5/1',
        banner_autoplay: shopfront?.banner_autoplay ?? false,
        category_art: shopfront?.category_art ?? 'photo',
        // Defaults chosen for an older API that does not send these yet: a
        // still rail is a complete experience, a drifting one somebody did
        // not ask for is not.
        category_scroll: shopfront?.category_scroll ?? false,
        category_scroll_seconds: shopfront?.category_scroll_seconds ?? 5,
      },
    };
  }

  async categories(sector?: Sector, signal?: AbortSignal): Promise<Category[]> {
    const { data } = await this.get<Category[]>(
      '/categories',
      { sector: sector === 'veterinary' ? sector : undefined },
      signal,
    );

    return data;
  }

  async searchProducts(
    params: {
      q?: string;
      category?: string;
      state?: string;
      sort?: 'price' | 'relevance' | 'newest';
      per_page?: number;
      page?: number;
      sector?: Sector;
      /** Veterinary only: the filter that splits the animal-health market. */
      species?: SpeciesKey;
    } = {},
    signal?: AbortSignal,
  ): Promise<{ products: ProductSummary[]; meta: PaginationMeta }> {
    const response = await this.get<ProductSummary[]>(
      '/products',
      { ...params, sector: params.sector === 'veterinary' ? params.sector : undefined },
      signal,
    );

    return {
      products: response.data,
      meta: (response.meta ?? {}) as PaginationMeta,
    };
  }

  async product(slug: string, state?: string, signal?: AbortSignal): Promise<Product> {
    const { data } = await this.get<Product>(
      `/products/${encodeURIComponent(slug)}`,
      { state },
      signal,
    );

    return data;
  }

  async pharmacies(
    params: { state?: string; q?: string; per_page?: number } = {},
    signal?: AbortSignal,
  ): Promise<{ pharmacies: Pharmacy[]; meta: PaginationMeta }> {
    const response = await this.get<Pharmacy[]>('/pharmacies', params, signal);

    return {
      pharmacies: response.data,
      meta: (response.meta ?? {}) as PaginationMeta,
    };
  }


  // -- the prescription board ----------------------------------------------

  /**
   * Upload the paper.
   *
   * Separate from creating the request so a buyer can photograph it, watch it
   * upload, and then write what they need — rather than filling a form and
   * losing it to a failed upload on a bad connection.
   *
   * Multipart by hand: `request()` sets a JSON content type and would turn the
   * FormData into the string "{}".
   */
  async uploadPrescription(file: File | Blob): Promise<{ id: number }> {
    const form = new FormData();
    form.append('file', file);

    const token = await this.tokenStore.get();

    const response = await this.doFetch(`${this.baseUrl}/prescriptions`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: form,
    });

    const payload = await this.parseJson(response);

    if (!response.ok) {
      throw new ApiError(response.status, this.asErrorBody(payload, response.status));
    }

    return (payload as ApiEnvelope<{ id: number }>).data;
  }

  /**
   * Who a prescription would reach, before it is sent.
   *
   * Its own call rather than part of the form, because the answer depends on
   * the buyer's delivery address — which they may not have set yet.
   */
  async reachablePharmacies(
    params: { sector?: Sector; address_id?: number; state?: string } = {},
    signal?: AbortSignal,
  ): Promise<{
    pharmacies: ReachablePharmacy[];
    maxRecipients: number;
    deliveringTo: string | null;
    state: string | null;
    states: StateWithCover[];
  }> {
    const response = await this.get<ReachablePharmacy[]>(
      '/quote-requests/reachable',
      params,
      signal,
    );

    const meta = (response.meta ?? {}) as {
      max_recipients?: number;
      delivering_to?: string | null;
      state?: string | null;
      states?: StateWithCover[];
    };

    return {
      pharmacies: response.data,
      maxRecipients: Number(meta.max_recipients ?? 5),
      deliveringTo: meta.delivering_to ?? null,
      state: meta.state ?? null,
      states: meta.states ?? [],
    };
  }

  async createQuoteRequest(input: {
    sector?: Sector;
    prescription_id?: number;
    wanted?: WantedItem[];
    note?: string;
    address_id?: number;
    species?: SpeciesKey;
    animal_note?: string;
    pharmacy_ids?: number[];
  }): Promise<QuoteRequestRow> {
    const { data } = await this.request<QuoteRequestRow>('/quote-requests', {
      method: 'POST',
      body: input,
    });

    return data;
  }

  async quoteRequests(signal?: AbortSignal): Promise<QuoteRequestRow[]> {
    const { data } = await this.get<QuoteRequestRow[]>('/quote-requests', undefined, signal);

    return data;
  }

  async quoteRequest(reference: string, signal?: AbortSignal): Promise<QuoteRequestDetail> {
    const { data } = await this.get<QuoteRequestDetail>(
      `/quote-requests/${encodeURIComponent(reference)}`,
      undefined,
      signal,
    );

    return data;
  }

  /**
   * Take a quote.
   *
   * Returns an ordinary `Order`. The client sends the buyer to the same
   * checkout as a cart — there is no separate prescription payment.
   */
  /**
   * What delivery would cost for this quote, to this address.
   *
   * Called as the buyer changes address, so the figure moves in front of them.
   * Accepting first and discovering the fee afterwards is the one thing this
   * flow must never do.
   */
  async quoteDelivery(
    reference: string,
    quoteReference: string,
    addressId?: number,
    signal?: AbortSignal,
  ): Promise<{ options: QuoteDeliveryOption[]; meta: QuoteDeliveryMeta }> {
    const response = await this.get<QuoteDeliveryOption[]>(
      `/quote-requests/${encodeURIComponent(reference)}/quotes/${encodeURIComponent(quoteReference)}/delivery`,
      { address_id: addressId },
      signal,
    );

    return { options: response.data, meta: (response.meta ?? {}) as unknown as QuoteDeliveryMeta };
  }

  async acceptQuote(
    reference: string,
    quoteReference: string,
    deliveryMethod: DeliveryMethod = 'standard',
    addressId?: number,
  ): Promise<Order> {
    const { data } = await this.request<Order>(
      `/quote-requests/${encodeURIComponent(reference)}/quotes/${encodeURIComponent(quoteReference)}/accept`,
      {
        method: 'POST',
        body: { delivery_method: deliveryMethod, address_id: addressId },
        idempotencyKey: `accept-${reference}-${quoteReference}-${addressId ?? 'default'}`,
      },
    );

    return data;
  }

  async cancelQuoteRequest(reference: string): Promise<QuoteRequestRow> {
    const { data } = await this.request<QuoteRequestRow>(
      `/quote-requests/${encodeURIComponent(reference)}/cancel`,
      { method: 'POST' },
    );

    return data;
  }

  // -- the prescription board (pharmacy) -----------------------------------

  async incomingQuoteRequests(
    signal?: AbortSignal,
  ): Promise<{ requests: IncomingQuoteRequest[]; waiting: number }> {
    const response = await this.get<IncomingQuoteRequest[]>(
      '/seller/quote-requests',
      undefined,
      signal,
    );

    return {
      requests: response.data,
      waiting: Number((response.meta as { waiting?: number } | undefined)?.waiting ?? 0),
    };
  }

  async incomingQuoteRequest(
    reference: string,
    signal?: AbortSignal,
  ): Promise<IncomingQuoteRequestDetail> {
    const { data } = await this.get<IncomingQuoteRequestDetail>(
      `/seller/quote-requests/${encodeURIComponent(reference)}`,
      undefined,
      signal,
    );

    return data;
  }

  /**
   * The prescription image, as a blob.
   *
   * Fetched rather than linked because it needs the bearer token, and because
   * every read is logged server-side — there is no unlogged path to this file
   * and no URL anybody can share (§9.15).
   */
  async prescriptionBlob(reference: string): Promise<Blob> {
    const token = await this.tokenStore.get();

    const response = await this.doFetch(
      `${this.baseUrl}/seller/quote-requests/${encodeURIComponent(reference)}/prescription`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );

    if (!response.ok) {
      throw new ApiError(response.status, {
        message: 'That prescription could not be opened.',
        errors: {},
        code: 'NOT_FOUND',
      });
    }

    return response.blob();
  }

  async submitQuote(
    reference: string,
    input: { lines: QuoteLineInput[]; note?: string; valid_hours?: number },
  ): Promise<SellerQuote> {
    const { data } = await this.request<SellerQuote>(
      `/seller/quote-requests/${encodeURIComponent(reference)}/quotes`,
      { method: 'POST', body: input },
    );

    return data;
  }

  async declineQuoteRequest(reference: string, reason?: string): Promise<IncomingQuoteRequest> {
    const { data } = await this.request<IncomingQuoteRequest>(
      `/seller/quote-requests/${encodeURIComponent(reference)}/decline`,
      { method: 'POST', body: { reason } },
    );

    return data;
  }

  // -- addresses -----------------------------------------------------------

  async addresses(signal?: AbortSignal): Promise<Address[]> {
    const { data } = await this.get<Address[]>('/addresses', undefined, signal);

    return data;
  }

  /**
   * Forget an address.
   *
   * Orders already placed keep the address they were placed WITH — the
   * sub-order snapshots it (§9.16), so a rider mid-journey never loses the
   * door they are heading for.
   */
  async deleteAddress(id: number): Promise<void> {
    await this.request(`/addresses/${id}`, { method: 'DELETE' });
  }

  async createAddress(address: NewAddress): Promise<Address> {
    const { data } = await this.request<Address>('/addresses', {
      method: 'POST',
      body: address,
    });

    return data;
  }

  // -- support enquiries ---------------------------------------------------
  //
  // Public: no login anywhere in here. An enquiry is opened with a name and a
  // contact, and everything after is found by the TOKEN that opening returns.
  // The token is passed as a header rather than in the path, so it never ends
  // up in a server access log or a browser history entry.

  async openEnquiry(name: string, contact: string): Promise<SupportThreadView> {
    const { data } = await this.request<SupportThreadView>('/support/enquiries', {
      method: 'POST',
      body: { name, contact },
    });

    return data;
  }

  async enquiry(token: string, signal?: AbortSignal): Promise<SupportThreadView> {
    const { data } = await this.request<SupportThreadView>('/support/enquiry', {
      headers: { 'X-Support-Token': token },
      signal,
    });

    return data;
  }

  /** Everything since message `after` — the shape a socket frame carries. */
  async enquiryPoll(token: string, after: number, signal?: AbortSignal): Promise<SupportPoll> {
    const { data } = await this.request<SupportPoll>(
      `/support/enquiry/poll?after=${encodeURIComponent(String(after))}`,
      { headers: { 'X-Support-Token': token }, signal },
    );

    return data;
  }

  async sendEnquiryMessage(token: string, body: string): Promise<SupportMessage> {
    const { data } = await this.request<SupportMessage>('/support/enquiry/messages', {
      method: 'POST',
      headers: { 'X-Support-Token': token },
      body: { body },
    });

    return data;
  }

  async enquiryTyping(token: string): Promise<void> {
    await this.request('/support/enquiry/typing', {
      method: 'POST',
      headers: { 'X-Support-Token': token },
    });
  }

  /** The visitor saying "you have answered me". Not the same as hiding it. */
  async closeEnquiry(token: string): Promise<void> {
    await this.request('/support/enquiry/close', {
      method: 'POST',
      headers: { 'X-Support-Token': token },
    });
  }

  // -- the support desk ----------------------------------------------------

  async supportDesk(
    params?: { queue?: string; status?: 'open' | 'closed' | 'recent' },
    signal?: AbortSignal,
  ): Promise<{ threads: SupportDeskThread[]; counts: { open: number; closed: number } }> {
    const response = await this.get<SupportDeskThread[]>('/admin/support', params, signal);

    return {
      threads: response.data,
      counts: (response as { meta?: { counts?: { open: number; closed: number } } }).meta
        ?.counts ?? { open: 0, closed: 0 },
    };
  }

  async supportDeskThread(id: number, signal?: AbortSignal): Promise<SupportDeskView> {
    const { data } = await this.get<SupportDeskView>(`/admin/support/${id}`, undefined, signal);

    return data;
  }

  async supportDeskPoll(
    id: number,
    after: number,
    signal?: AbortSignal,
  ): Promise<SupportDeskPoll> {
    const { data } = await this.get<SupportDeskPoll>(
      `/admin/support/${id}/poll`,
      { after: String(after) },
      signal,
    );

    return data;
  }

  async replyToSupport(id: number, body: string): Promise<SupportMessage> {
    const { data } = await this.request<SupportMessage>(`/admin/support/${id}/messages`, {
      method: 'POST',
      body: { body },
    });

    return data;
  }

  async supportDeskTyping(id: number): Promise<void> {
    await this.request(`/admin/support/${id}/typing`, { method: 'POST' });
  }

  async closeSupportThread(id: number): Promise<void> {
    await this.request(`/admin/support/${id}/close`, { method: 'POST' });
  }

  // -- cart & orders (§10 Phase 2) -----------------------------------------

  /**
   * Price a cart.
   *
   * The client sends listing ids and quantities and nothing else. It does not
   * send prices — a client-supplied price is a client-supplied discount, and
   * this is a marketplace where the money moves to a third party.
   */
  async priceCart(input: {
    items: CartLine[];
    deliveryMethod?: DeliveryMethod;
    creditKobo?: number;
  }): Promise<PricedCart> {
    const { data } = await this.request<PricedCart>('/cart/price', {
      method: 'POST',
      body: {
        items: input.items,
        delivery_method: input.deliveryMethod ?? 'standard',
        credit_kobo: input.creditKobo ?? 0,
      },
    });

    return data;
  }

  /**
   * Place an order.
   *
   * `idempotencyKey` is REQUIRED and the API refuses without it (§9.14).
   * Generate ONE key when the buyer opens checkout and reuse it for every
   * retry of that same attempt — a fresh key per tap is exactly the bug
   * idempotency exists to prevent.
   */
  async placeOrder(input: {
    items: CartLine[];
    addressId?: number | null;
    deliveryMethod?: DeliveryMethod;
    creditKobo?: number;
    idempotencyKey: string;
  }): Promise<Order> {
    const { data } = await this.request<Order>('/orders', {
      method: 'POST',
      idempotencyKey: input.idempotencyKey,
      body: {
        items: input.items,
        address_id: input.addressId ?? null,
        delivery_method: input.deliveryMethod ?? 'standard',
        credit_kobo: input.creditKobo ?? 0,
      },
    });

    return data;
  }

  async orders(signal?: AbortSignal): Promise<Order[]> {
    const { data } = await this.get<Order[]>('/orders', undefined, signal);

    return data;
  }

  async order(reference: string, signal?: AbortSignal): Promise<Order> {
    const { data } = await this.get<Order>(`/orders/${reference}`, undefined, signal);

    return data;
  }

  // -- admin: settings & CMS ------------------------------------------------

  async adminSettings(signal?: AbortSignal): Promise<SettingsPayload> {
    const { data } = await this.get<SettingsPayload>('/admin/settings', undefined, signal);

    return data;
  }

  /**
   * §9.11: `reason` is required for settings that move money or change what
   * buyers are promised. The API refuses without it rather than trusting the
   * console to ask.
   */
  async updateAdminSettings(
    values: Record<string, unknown>,
    reason?: string,
  ): Promise<{ changed: Record<string, { from: unknown; to: unknown }>; values: Record<string, unknown> }> {
    const { data } = await this.request<{
      changed: Record<string, { from: unknown; to: unknown }>;
      values: Record<string, unknown>;
    }>('/admin/settings', { method: 'PATCH', body: { values, reason } });

    return data;
  }

  // -- pharmacy onboarding (§6) --------------------------------------------

  /**
   * Register a pharmacy.
   *
   * Public: a prospective pharmacy has no ChiCare account yet. This creates the
   * console account and the pharmacy together, with the email-and-password
   * credential the seller console signs in with.
   */
  async registerPharmacy(input: Record<string, unknown>): Promise<SellerPharmacy> {
    const { data } = await this.request<SellerPharmacy>('/pharmacies/register', {
      method: 'POST',
      body: input,
    });

    return data;
  }

  async sellerPharmacy(
    signal?: AbortSignal,
  ): Promise<{ pharmacy: SellerPharmacy | null; meta: SellerPharmacyMeta }> {
    const response = await this.get<SellerPharmacy | null>('/seller/pharmacy', undefined, signal);

    return {
      pharmacy: response.data,
      meta: (response.meta ?? {}) as unknown as SellerPharmacyMeta,
    };
  }

  async updateSellerPharmacy(input: Record<string, unknown>): Promise<SellerPharmacy> {
    const { data } = await this.request<SellerPharmacy>('/seller/pharmacy', {
      method: 'PATCH',
      body: input,
    });

    return data;
  }

  async submitPharmacy(): Promise<SellerPharmacy> {
    const { data } = await this.request<SellerPharmacy>('/seller/pharmacy/submit', {
      method: 'POST',
    });

    return data;
  }

  // ── Dashboards ──────────────────────────────────────────────────────────

  /**
   * What this pharmacy has been doing.
   *
   * `days` is clamped server-side to 7–90: a seller asking for five years of
   * daily points would be asking a phone to draw 1,800 of them.
   */
  async sellerDashboard(days = 30, signal?: AbortSignal): Promise<SellerDashboard> {
    const { data } = await this.get<SellerDashboard>('/seller/dashboard', { days }, signal);

    return data;
  }

  /** Whether ChiCare is working. Reports only — nothing here acts. */
  async adminDashboard(days = 30, signal?: AbortSignal): Promise<AdminDashboard> {
    const { data } = await this.get<AdminDashboard>('/admin/dashboard', { days }, signal);

    return data;
  }

  // ── Staff accounts ──────────────────────────────────────────────────────

  async staffAccounts(
    signal?: AbortSignal,
  ): Promise<{ staff: StaffAccount[]; roles: Array<{ id: number; name: string }> }> {
    const response = await this.get<StaffAccount[]>('/admin/staff', undefined, signal);
    const meta = (response.meta ?? {}) as { roles?: Array<{ id: number; name: string }> };

    return { staff: response.data, roles: meta.roles ?? [] };
  }

  async createStaffAccount(input: {
    name: string;
    email: string;
    phone?: string;
    password: string;
    account_type: string;
    staff_role_id: number | null;
  }): Promise<StaffAccount> {
    const { data } = await this.request<StaffAccount>('/admin/staff', {
      method: 'POST',
      body: input,
    });

    return data;
  }

  /** `password` empty means "leave it alone" — it is not sent. */
  async updateStaffAccount(
    id: number,
    input: {
      name: string;
      email: string;
      phone?: string;
      password?: string;
      account_type: string;
      staff_role_id: number | null;
      status?: string;
    },
  ): Promise<StaffAccount> {
    const { data } = await this.request<StaffAccount>(`/admin/staff/${id}`, {
      method: 'PATCH',
      body: input,
    });

    return data;
  }

  /** Revokes access. Deletes outright only if the account never did anything. */
  async removeStaffAccount(id: number): Promise<{ deleted: boolean; revoked: boolean }> {
    const { data } = await this.request<{ deleted: boolean; revoked: boolean }>(
      `/admin/staff/${id}`,
      { method: 'DELETE' },
    );

    return data;
  }

  // ── Prohibited medicines ────────────────────────────────────────────────

  async prohibitions(
    sector?: string,
    signal?: AbortSignal,
  ): Promise<{ bans: ProhibitionRow[]; inForce: number; lifted: number }> {
    const response = await this.get<ProhibitionRow[]>(
      '/admin/prohibitions',
      sector ? { sector } : undefined,
      signal,
    );
    const meta = (response.meta ?? {}) as { in_force?: number; lifted?: number };

    return {
      bans: response.data,
      inForce: Number(meta.in_force ?? 0),
      lifted: Number(meta.lifted ?? 0),
    };
  }

  /** What a ban would seize, before it seizes it. */
  async previewProhibition(productId: number): Promise<ProhibitionPreview> {
    const { data } = await this.request<ProhibitionPreview>('/admin/prohibitions/preview', {
      method: 'POST',
      body: { product_id: productId },
    });

    return data;
  }

  async issueProhibition(input: {
    product_id?: number | null;
    name: string;
    generic_name?: string;
    manufacturer?: string;
    nafdac_reg_no?: string;
    sector: string;
    reason: string;
    authority: string;
    reference_no?: string;
    announced_on?: string;
  }): Promise<ProhibitionRow> {
    const { data } = await this.request<ProhibitionRow>('/admin/prohibitions', {
      method: 'POST',
      body: input,
      idempotencyKey: newIdempotencyKey(),
    });

    return data;
  }

  async liftProhibition(reference: string, reason: string): Promise<ProhibitionRow> {
    const { data } = await this.request<ProhibitionRow>(
      `/admin/prohibitions/${reference}/lift`,
      { method: 'POST', body: { reason } },
    );

    return data;
  }

  // ── Audit disclosure ────────────────────────────────────────────────────

  async auditDisclosures(
    signal?: AbortSignal,
  ): Promise<{ disclosures: AuditDisclosureRow[]; domains: Record<string, string> }> {
    const response = await this.get<AuditDisclosureRow[]>(
      '/admin/audit-disclosures',
      undefined,
      signal,
    );
    const meta = (response.meta ?? {}) as { domains?: Record<string, string> };

    return { disclosures: response.data, domains: meta.domains ?? {} };
  }

  async previewAuditDisclosure(input: {
    period_from: string;
    period_to: string;
    scope?: string[];
  }): Promise<AuditDisclosurePreview> {
    const { data } = await this.request<AuditDisclosurePreview>(
      '/admin/audit-disclosures/preview',
      { method: 'POST', body: input },
    );

    return data;
  }

  async authoriseAuditDisclosure(input: {
    requested_by: string;
    authority_reference?: string;
    reason: string;
    period_from: string;
    period_to: string;
    scope?: string[];
    includes_personal_data?: boolean;
  }): Promise<AuditDisclosureRow> {
    const { data } = await this.request<AuditDisclosureRow>('/admin/audit-disclosures', {
      method: 'POST',
      body: input,
    });

    return data;
  }

  /**
   * The file itself.
   *
   * Fetched rather than linked, because the endpoint needs a bearer token and
   * an <a href> cannot carry one. The digest comes back in a header so the
   * caller can show it beside the download.
   */
  async downloadAuditDisclosure(
    reference: string,
  ): Promise<{ blob: Blob; sha256: string | null; filename: string }> {
    const token = await this.tokenStore.get();

    const response = await this.doFetch(
      `${this.baseUrl}/admin/audit-disclosures/${reference}/download`,
      {
        headers: {
          Accept: 'application/x-ndjson',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      },
    );

    if (!response.ok) {
      const payload = await this.parseJson(response);

      throw new ApiError(response.status, this.asErrorBody(payload, response.status));
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const match = /filename="?([^";]+)"?/.exec(disposition);

    return {
      blob: await response.blob(),
      sha256: response.headers.get('x-chicare-sha256'),
      filename: match?.[1] ?? `${reference}.ndjson`,
    };
  }

  // ── Sponsored placement ─────────────────────────────────────────────────

  async sponsorshipRates(signal?: AbortSignal): Promise<{
    rates: SponsorshipRate[];
    placements: PlacementOption[];
    units: string[];
    sectors: string[];
  }> {
    const response = await this.get<SponsorshipRate[]>(
      '/admin/sponsorships/rates',
      undefined,
      signal,
    );
    const meta = (response.meta ?? {}) as {
      placements?: PlacementOption[];
      units?: string[];
      sectors?: string[];
    };

    return {
      rates: response.data,
      placements: meta.placements ?? [],
      units: meta.units ?? [],
      sectors: meta.sectors ?? [],
    };
  }

  async setSponsorshipRate(input: {
    placement: string;
    sector: string;
    unit: string;
    price_kobo: number;
    is_active?: boolean;
  }): Promise<SponsorshipRate> {
    const { data } = await this.request<SponsorshipRate>('/admin/sponsorships/rates', {
      method: 'PUT',
      body: input,
    });

    return data;
  }

  async sponsorships(
    signal?: AbortSignal,
  ): Promise<{
    deals: SponsorshipRow[];
    live: number;
    liveValueDisplay: string;
    soldThisYearDisplay: string;
  }> {
    const response = await this.get<SponsorshipRow[]>('/admin/sponsorships', undefined, signal);
    const meta = (response.meta ?? {}) as {
      live?: number;
      live_value_display?: string;
      sold_this_year_display?: string;
    };

    return {
      deals: response.data,
      live: Number(meta.live ?? 0),
      liveValueDisplay: String(meta.live_value_display ?? '₦0'),
      soldThisYearDisplay: String(meta.sold_this_year_display ?? '₦0'),
    };
  }

  async quoteSponsorship(input: {
    placement: string;
    sector: string;
    unit: string;
    units: number;
    starts_on: string;
  }): Promise<SponsorshipQuote> {
    const { data } = await this.request<SponsorshipQuote>('/admin/sponsorships/quote', {
      method: 'POST',
      body: input,
    });

    return data;
  }

  async sellSponsorship(input: {
    pharmacy_id: number;
    placement: string;
    sector: string;
    unit: string;
    units: number;
    starts_on: string;
    category_id?: number | null;
    note?: string;
  }): Promise<SponsorshipRow> {
    const { data } = await this.request<SponsorshipRow>('/admin/sponsorships', {
      method: 'POST',
      body: input,
    });

    return data;
  }

  async activateSponsorship(reference: string): Promise<SponsorshipRow> {
    const { data } = await this.request<SponsorshipRow>(
      `/admin/sponsorships/${reference}/activate`,
      { method: 'POST', body: {} },
    );

    return data;
  }

  async cancelSponsorship(reference: string, reason: string): Promise<SponsorshipRow> {
    const { data } = await this.request<SponsorshipRow>(
      `/admin/sponsorships/${reference}/cancel`,
      { method: 'POST', body: { reason } },
    );

    return data;
  }

  // ── Inventory: goods receiving ──────────────────────────────────────────

  async goodsReceipts(
    sector: string,
    signal?: AbortSignal,
  ): Promise<{
    receipts: GoodsReceiptRow[];
    sector: string;
    sectors: SectorOption[];
    drafts: number;
  }> {
    const response = await this.get<GoodsReceiptRow[]>(
      '/seller/goods-receipts',
      { sector },
      signal,
    );
    const meta = (response.meta ?? {}) as {
      sector?: string;
      sectors?: SectorOption[];
      drafts?: number;
    };

    return {
      receipts: response.data,
      sector: String(meta.sector ?? sector),
      sectors: meta.sectors ?? [],
      drafts: Number(meta.drafts ?? 0),
    };
  }

  async goodsReceipt(reference: string, signal?: AbortSignal): Promise<GoodsReceipt> {
    const { data } = await this.get<GoodsReceipt>(
      `/seller/goods-receipts/${reference}`,
      undefined,
      signal,
    );

    return data;
  }

  async startGoodsReceipt(input: {
    sector: string;
    supplier_id?: number | null;
    invoice_no?: string;
    received_on: string;
    note?: string;
  }): Promise<GoodsReceiptRow> {
    const { data } = await this.request<GoodsReceiptRow>('/seller/goods-receipts', {
      method: 'POST',
      body: input,
    });

    return data;
  }

  async addGoodsReceiptLine(
    reference: string,
    input: {
      listing_id: number;
      batch_no: string;
      expiry_date: string;
      quantity: number;
      unit_cost_kobo?: number;
    },
  ): Promise<{ id: number }> {
    const { data } = await this.request<{ id: number }>(
      `/seller/goods-receipts/${reference}/lines`,
      { method: 'POST', body: input },
    );

    return data;
  }

  async removeGoodsReceiptLine(reference: string, lineId: number): Promise<{ removed: boolean }> {
    const { data } = await this.request<{ removed: boolean }>(
      `/seller/goods-receipts/${reference}/lines/${lineId}`,
      { method: 'DELETE' },
    );

    return data;
  }

  /**
   * Book it in. Everything becomes true at once, or nothing does.
   *
   * The validation errors from this call are the most valuable thing in the
   * feature: short-dated and expired stock is refused while the driver is
   * still outside and it can go back on the van.
   */
  async postGoodsReceipt(reference: string): Promise<GoodsReceiptRow> {
    const { data } = await this.request<GoodsReceiptRow>(
      `/seller/goods-receipts/${reference}/post`,
      { method: 'POST', body: {}, idempotencyKey: newIdempotencyKey() },
    );

    return data;
  }

  async discardGoodsReceipt(reference: string): Promise<{ deleted: boolean }> {
    const { data } = await this.request<{ deleted: boolean }>(
      `/seller/goods-receipts/${reference}`,
      { method: 'DELETE' },
    );

    return data;
  }

  // ── A seller's own team ─────────────────────────────────────────────────

  async sellerStaff(
    signal?: AbortSignal,
  ): Promise<{
    staff: SellerStaffRow[];
    catalogue: SellerPermissionCatalogue;
    owner: { name: string; email: string | null };
  }> {
    const response = await this.get<SellerStaffRow[]>('/seller/staff', undefined, signal);
    const meta = (response.meta ?? {}) as {
      catalogue?: SellerPermissionCatalogue;
      owner?: { name: string; email: string | null };
    };

    return {
      staff: response.data,
      catalogue: meta.catalogue ?? {},
      owner: meta.owner ?? { name: '', email: null },
    };
  }

  async createSellerStaff(input: {
    name: string;
    email: string;
    phone?: string;
    password: string;
    title?: string;
    permissions: string[];
    note?: string;
  }): Promise<SellerStaffRow> {
    const { data } = await this.request<SellerStaffRow>('/seller/staff', {
      method: 'POST',
      body: input,
    });

    return data;
  }

  async updateSellerStaff(
    id: number,
    input: {
      name: string;
      title?: string;
      permissions: string[];
      password?: string;
      status?: string;
      note?: string;
    },
  ): Promise<SellerStaffRow> {
    const { data } = await this.request<SellerStaffRow>(`/seller/staff/${id}`, {
      method: 'PATCH',
      body: input,
    });

    return data;
  }

  async revokeSellerStaff(id: number): Promise<{ revoked: boolean }> {
    const { data } = await this.request<{ revoked: boolean }>(`/seller/staff/${id}`, {
      method: 'DELETE',
    });

    return data;
  }

  // ── Inventory: recalls ──────────────────────────────────────────────────

  async sellerRecalls(
    signal?: AbortSignal,
  ): Promise<{ recalls: SellerRecallRow[]; outstanding: number }> {
    const response = await this.get<SellerRecallRow[]>('/seller/recalls', undefined, signal);
    const meta = (response.meta ?? {}) as { outstanding?: number };

    return { recalls: response.data, outstanding: Number(meta.outstanding ?? 0) };
  }

  async acknowledgeRecall(reference: string, note: string): Promise<SellerRecallRow> {
    const { data } = await this.request<SellerRecallRow>(
      `/seller/recalls/${reference}/acknowledge`,
      { method: 'POST', body: { note } },
    );

    return data;
  }

  async recalls(signal?: AbortSignal): Promise<{ recalls: RecallRow[]; open: number }> {
    const response = await this.get<RecallRow[]>('/admin/recalls', undefined, signal);
    const meta = (response.meta ?? {}) as { open?: number };

    return { recalls: response.data, open: Number(meta.open ?? 0) };
  }

  async recall(reference: string, signal?: AbortSignal): Promise<Recall> {
    const { data } = await this.get<Recall>(`/admin/recalls/${reference}`, undefined, signal);

    return data;
  }

  async previewRecall(input: {
    product_id: number;
    batch_no: string;
    sector: string;
  }): Promise<RecallPreview> {
    const { data } = await this.request<RecallPreview>('/admin/recalls/preview', {
      method: 'POST',
      body: input,
    });

    return data;
  }

  async issueRecall(input: {
    product_id: number;
    batch_no: string;
    sector: string;
    severity: string;
    reason: string;
    source?: string;
  }): Promise<RecallRow> {
    const { data } = await this.request<RecallRow>('/admin/recalls', {
      method: 'POST',
      body: input,
      idempotencyKey: newIdempotencyKey(),
    });

    return data;
  }

  async closeRecall(reference: string, note: string): Promise<RecallRow> {
    const { data } = await this.request<RecallRow>(`/admin/recalls/${reference}/close`, {
      method: 'POST',
      body: { note },
    });

    return data;
  }

  // ── Staff roles ─────────────────────────────────────────────────────────

  async staffRoles(
    signal?: AbortSignal,
  ): Promise<{ roles: StaffRole[]; catalogue: PermissionCatalogue }> {
    const response = await this.get<StaffRole[]>('/admin/staff-roles', undefined, signal);
    const meta = (response.meta ?? {}) as { catalogue?: PermissionCatalogue };

    return { roles: response.data, catalogue: meta.catalogue ?? {} };
  }

  async createStaffRole(input: {
    name: string;
    description?: string;
    permissions: string[];
  }): Promise<StaffRole> {
    const { data } = await this.request<StaffRole>('/admin/staff-roles', {
      method: 'POST',
      body: input,
    });

    return data;
  }

  async updateStaffRole(
    id: number,
    input: { name: string; description?: string; permissions: string[] },
  ): Promise<StaffRole> {
    const { data } = await this.request<StaffRole>(`/admin/staff-roles/${id}`, {
      method: 'PATCH',
      body: input,
    });

    return data;
  }

  async deleteStaffRole(id: number): Promise<{ deleted: boolean }> {
    const { data } = await this.request<{ deleted: boolean }>(`/admin/staff-roles/${id}`, {
      method: 'DELETE',
    });

    return data;
  }

  /** Put somebody in a job, or take them out of one. `null` removes it. */
  async assignStaffRole(
    userId: number,
    staffRoleId: number | null,
  ): Promise<{ user_id: number; staff_role_id: number | null; permissions: string[] }> {
    const { data } = await this.request<{
      user_id: number;
      staff_role_id: number | null;
      permissions: string[];
    }>(`/admin/people/${userId}/staff-role`, {
      method: 'POST',
      body: { staff_role_id: staffRoleId },
    });

    return data;
  }

  // ── Complaints, from the seller's side ──────────────────────────────────

  async sellerComplaints(
    signal?: AbortSignal,
  ): Promise<{ complaints: SellerComplaintRow[] } & SellerComplaintSummary> {
    const response = await this.get<SellerComplaintRow[]>('/seller/complaints', undefined, signal);
    const meta = (response.meta ?? {}) as {
      open?: number;
      unanswered?: number;
      answered?: number;
      settled?: number;
      upheld?: number;
      not_upheld?: number;
      refunded_kobo?: number;
      refunded_display?: string;
    };

    return {
      complaints: response.data,
      open: Number(meta.open ?? 0),
      unanswered: Number(meta.unanswered ?? 0),
      answered: Number(meta.answered ?? 0),
      settled: Number(meta.settled ?? 0),
      upheld: Number(meta.upheld ?? 0),
      notUpheld: Number(meta.not_upheld ?? 0),
      refundedKobo: Number(meta.refunded_kobo ?? 0),
      refundedDisplay: String(meta.refunded_display ?? '₦0'),
    };
  }

  async sellerComplaint(reference: string, signal?: AbortSignal): Promise<SellerComplaint> {
    const { data } = await this.get<SellerComplaint>(
      `/seller/complaints/${reference}`,
      undefined,
      signal,
    );

    return data;
  }

  /** The pharmacy's side, read before anybody decides. */
  async respondToComplaint(reference: string, response: string): Promise<SellerComplaintRow> {
    const { data } = await this.request<SellerComplaintRow>(
      `/seller/complaints/${reference}/respond`,
      { method: 'POST', body: { response } },
    );

    return data;
  }

  // ── The clinical desk ───────────────────────────────────────────────────

  async clinicalReports(
    status = 'waiting',
    signal?: AbortSignal,
  ): Promise<{ reports: ClinicalReportRow[]; waiting: number; overdue: number }> {
    const response = await this.get<ClinicalReportRow[]>('/pharmacist/reports', { status }, signal);
    const meta = (response.meta ?? {}) as { waiting?: number; overdue?: number };

    return {
      reports: response.data,
      waiting: Number(meta.waiting ?? 0),
      overdue: Number(meta.overdue ?? 0),
    };
  }

  async clinicalReport(reference: string, signal?: AbortSignal): Promise<ClinicalReport> {
    const { data } = await this.get<ClinicalReport>(
      `/pharmacist/reports/${reference}`,
      undefined,
      signal,
    );

    return data;
  }

  /**
   * A photograph the buyer attached.
   *
   * Streamed from the private disk behind the same gate as the report, never a
   * public URL — refund evidence is a photograph of somebody's body often
   * enough that a guessable link would be the worst kind of leak.
   */
  clinicalPhotoUrl(reference: string, index: number): string {
    return `${this.baseUrl}/pharmacist/reports/${reference}/photo/${index}`;
  }

  async approveClinicalReport(reference: string, note?: string): Promise<ClinicalReportRow> {
    const { data } = await this.request<ClinicalReportRow>(
      `/pharmacist/reports/${reference}/approve`,
      { method: 'POST', body: { note } },
    );

    return data;
  }

  async declineClinicalReport(reference: string, note: string): Promise<ClinicalReportRow> {
    const { data } = await this.request<ClinicalReportRow>(
      `/pharmacist/reports/${reference}/decline`,
      { method: 'POST', body: { note } },
    );

    return data;
  }

  // ── The bell ────────────────────────────────────────────────────────────

  /**
   * The list and the unread count together.
   *
   * One call rather than two: the bell and the panel are read at the same
   * moment, and a separate request for a single number is a second round trip
   * on a connection that cannot spare it.
   */
  async notifications(
    unreadOnly = false,
    signal?: AbortSignal,
  ): Promise<{ notifications: AppNotification[]; unread: number }> {
    const response = await this.get<AppNotification[]>(
      '/notifications',
      unreadOnly ? { unread: 1 } : undefined,
      signal,
    );

    return {
      notifications: response.data,
      unread: Number((response.meta as { unread?: number } | undefined)?.unread ?? 0),
    };
  }

  async unreadNotificationCount(signal?: AbortSignal): Promise<number> {
    const { data } = await this.get<{ unread: number }>('/notifications/unread-count', undefined, signal);

    return data.unread;
  }

  async readNotification(id: number): Promise<AppNotification> {
    const { data } = await this.request<AppNotification>(`/notifications/${id}/read`, {
      method: 'POST',
    });

    return data;
  }

  async readAllNotifications(): Promise<{ cleared: number }> {
    const { data } = await this.request<{ cleared: number }>('/notifications/read-all', {
      method: 'POST',
    });

    return data;
  }

  /**
   * A buyer's own photograph, for the account screen.
   *
   * Multipart by hand for the same reason as `uploadPharmacyDocument` below:
   * `request()` sets a JSON content type and stringifies the body, which turns
   * a FormData into the string "{}" and sends an upload with no file in it.
   */
  async uploadAvatar(file: File | Blob): Promise<User> {
    const form = new FormData();
    form.append('file', file);

    const token = await this.tokenStore.get();

    const response = await this.doFetch(`${this.baseUrl}/auth/avatar`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: form,
    });

    const payload = await this.parseJson(response);

    if (!response.ok) {
      throw new ApiError(response.status, this.asErrorBody(payload, response.status));
    }

    return (payload as ApiEnvelope<{ user: User }>).data.user;
  }

  /** Take the photograph off. Whatever they picked before comes back. */
  async removeAvatar(): Promise<User> {
    const { data } = await this.request<{ user: User }>('/auth/avatar', { method: 'DELETE' });

    return data.user;
  }

  /**
   * Upload a licence, certificate or premises photo.
   *
   * Deliberately does NOT go through `request()`, which sets a JSON content
   * type and stringifies the body — that would turn a FormData into the string
   * "{}" and the server would see an upload with no file in it. Multipart needs
   * the browser to set its own boundary, so the fetch is made by hand.
   */
  async uploadPharmacyDocument(type: string, file: File | Blob): Promise<SellerPharmacy> {
    const form = new FormData();
    form.append('type', type);
    form.append('file', file);

    const token = await this.tokenStore.get();

    const response = await this.doFetch(`${this.baseUrl}/seller/pharmacy/documents`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: form,
    });

    const payload = await this.parseJson(response);

    if (!response.ok) {
      throw new ApiError(response.status, this.asErrorBody(payload, response.status));
    }

    return (payload as ApiEnvelope<SellerPharmacy>).data;
  }

  /**
   * Send in a renewed licence.
   *
   * Works while the pharmacy is live or suspended, unlike every other write on
   * the registration — which is the whole point. An expired licence takes the
   * seller's entire shelf off sale, and the screen that could fix it used to be
   * the one screen they were locked out of.
   *
   * Multipart by hand, for the same reason as `uploadPharmacyDocument`.
   */
  async submitLicenceRenewal(input: {
    sector?: Sector;
    licence_no: string;
    expiry_date: string;
    file: File | Blob;
  }): Promise<{ pharmacy: SellerPharmacy; meta: SellerPharmacyMeta }> {
    const form = new FormData();

    if (input.sector) form.append('sector', input.sector);
    form.append('licence_no', input.licence_no);
    form.append('expiry_date', input.expiry_date);
    form.append('file', input.file);

    const token = await this.tokenStore.get();

    const response = await this.doFetch(`${this.baseUrl}/seller/pharmacy/licence-renewals`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: form,
    });

    const payload = await this.parseJson(response);

    if (!response.ok) {
      throw new ApiError(response.status, this.asErrorBody(payload, response.status));
    }

    const envelope = payload as ApiEnvelope<SellerPharmacy>;

    return {
      pharmacy: envelope.data,
      meta: (envelope.meta ?? {}) as unknown as SellerPharmacyMeta,
    };
  }

  /**
   * The prescription board, for oversight.
   *
   * Read-only: there is no companion method to quote or accept on somebody's
   * behalf, because ChiCare is the pharmacy's agent and never the seller.
   */
  async quoteBoard(
    status = 'live',
    signal?: AbortSignal,
  ): Promise<{ requests: QuoteBoardRow[]; meta: QuoteBoardMeta }> {
    const response = await this.get<QuoteBoardRow[]>('/admin/quote-board', { status }, signal);

    return { requests: response.data, meta: (response.meta ?? {}) as unknown as QuoteBoardMeta };
  }

  // -- delivery zones (admin) ----------------------------------------------

  /**
   * Zones and their weight bands.
   *
   * Admin-owned because the people who know what a rider will take to cross
   * Kubwa are in operations, not engineering — and the day fuel moves is not a
   * day to be waiting for a release.
   */
  async deliveryZones(
    signal?: AbortSignal,
  ): Promise<{ zones: DeliveryZoneRow[]; meta: DeliveryZoneMeta }> {
    const response = await this.get<DeliveryZoneRow[]>('/admin/delivery-zones', undefined, signal);

    return { zones: response.data, meta: (response.meta ?? {}) as unknown as DeliveryZoneMeta };
  }

  async createDeliveryZone(input: DeliveryZoneInput): Promise<DeliveryZoneRow> {
    const { data } = await this.request<DeliveryZoneRow>('/admin/delivery-zones', {
      method: 'POST',
      body: input,
    });

    return data;
  }

  async updateDeliveryZone(
    id: number,
    input: Partial<DeliveryZoneInput>,
  ): Promise<DeliveryZoneRow> {
    const { data } = await this.request<DeliveryZoneRow>(`/admin/delivery-zones/${id}`, {
      method: 'PATCH',
      body: input,
    });

    return data;
  }

  async deleteDeliveryZone(id: number, reason?: string): Promise<void> {
    await this.request(`/admin/delivery-zones/${id}`, { method: 'DELETE', body: { reason } });
  }

  /**
   * Replace a zone's bands in one go.
   *
   * All of them together, because bands only mean anything as a set: a gap
   * between two is a weight nothing prices, and that is far easier to catch
   * here than to debug from a delivery fee somebody is querying.
   */
  async setDeliveryBands(
    id: number,
    rates: DeliveryBand[],
    reason?: string,
  ): Promise<DeliveryZoneRow> {
    const { data } = await this.request<DeliveryZoneRow>(`/admin/delivery-zones/${id}/rates`, {
      method: 'PUT',
      body: { rates, reason },
    });

    return data;
  }

  // -- pharmacy approval (admin) -------------------------------------------

  /**
   * Licence renewals waiting to be read.
   *
   * Worked ahead of new registrations: a pharmacy waiting on a first approval
   * has never sold anything, while one waiting on a renewal is dark.
   */
  async licenceRenewals(
    status = 'pending',
    signal?: AbortSignal,
  ): Promise<{ renewals: LicenceRenewalRow[]; pending: number }> {
    const response = await this.get<LicenceRenewalRow[]>('/admin/pharmacies/renewals', { status }, signal);

    return {
      renewals: response.data,
      pending: Number((response.meta as { pending?: number } | undefined)?.pending ?? 0),
    };
  }

  async approveLicenceRenewal(
    pharmacyId: number,
    renewalId: number,
    reason: string,
  ): Promise<LicenceRenewalRow> {
    const { data } = await this.request<LicenceRenewalRow>(
      `/admin/pharmacies/${pharmacyId}/renewals/${renewalId}/approve`,
      { method: 'POST', body: { reason } },
    );

    return data;
  }

  async rejectLicenceRenewal(
    pharmacyId: number,
    renewalId: number,
    note: string,
  ): Promise<LicenceRenewalRow> {
    const { data } = await this.request<LicenceRenewalRow>(
      `/admin/pharmacies/${pharmacyId}/renewals/${renewalId}/reject`,
      { method: 'POST', body: { note } },
    );

    return data;
  }

  async pharmacyApprovals(
    status = 'pending_approval',
    query = '',
    signal?: AbortSignal,
  ): Promise<{ pharmacies: PharmacyApprovalRow[]; counts: Record<string, number>; matched: number }> {
    const response = await this.get<PharmacyApprovalRow[]>(
      '/admin/pharmacies',
      { status, q: query || undefined },
      signal,
    );

    const meta = (response.meta ?? {}) as { counts?: Record<string, number>; matched?: number };

    return {
      pharmacies: response.data,
      counts: meta.counts ?? {},
      matched: meta.matched ?? response.data.length,
    };
  }

  async pharmacyApproval(
    id: number,
    signal?: AbortSignal,
  ): Promise<{ pharmacy: PharmacyApprovalDetail; meta: PharmacyApprovalMeta }> {
    const response = await this.get<PharmacyApprovalDetail>(
      `/admin/pharmacies/${id}`,
      undefined,
      signal,
    );

    return {
      pharmacy: response.data,
      meta: (response.meta ?? {}) as unknown as PharmacyApprovalMeta,
    };
  }

  /** The document itself, fetched with the bearer token and never a public URL. */
  async pharmacyDocumentBlob(pharmacyId: number, documentId: number): Promise<Blob> {
    const token = await this.tokenStore.get();

    const response = await this.doFetch(
      `${this.baseUrl}/admin/pharmacies/${pharmacyId}/documents/${documentId}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );

    if (!response.ok) {
      throw new Error('That document could not be opened.');
    }

    return response.blob();
  }

  async reviewPharmacyDocument(
    pharmacyId: number,
    documentId: number,
    input: { status: 'approved' | 'rejected'; note?: string },
  ): Promise<PharmacyApprovalMeta> {
    const response = await this.request<{ status: string }>(
      `/admin/pharmacies/${pharmacyId}/documents/${documentId}/review`,
      { method: 'POST', body: input },
    );

    return (response.meta ?? {}) as unknown as PharmacyApprovalMeta;
  }

  /** §9.11: the written reason is the only record of what was checked. */
  async approvePharmacy(id: number, reason: string): Promise<PharmacyApprovalRow> {
    const { data } = await this.request<PharmacyApprovalRow>(`/admin/pharmacies/${id}/approve`, {
      method: 'POST',
      body: { reason },
    });

    return data;
  }

  async rejectPharmacy(id: number, reason: string): Promise<PharmacyApprovalRow> {
    const { data } = await this.request<PharmacyApprovalRow>(`/admin/pharmacies/${id}/reject`, {
      method: 'POST',
      body: { reason },
    });

    return data;
  }

  async setPharmacyStatus(
    id: number,
    status: 'active' | 'suspended',
    reason: string,
  ): Promise<PharmacyApprovalRow> {
    const { data } = await this.request<PharmacyApprovalRow>(`/admin/pharmacies/${id}/status`, {
      method: 'POST',
      body: { status, reason },
    });

    return data;
  }

  // -- seller listings & stock (§9.1, §9.3) --------------------------------

  async sellerListings(
    signal?: AbortSignal,
  ): Promise<{ listings: SellerListingRow[]; meta: SellerListingsMeta }> {
    const response = await this.get<SellerListingRow[]>('/seller/listings', undefined, signal);

    return {
      listings: response.data,
      meta: (response.meta ?? {}) as unknown as SellerListingsMeta,
    };
  }

  async sellerListing(
    id: number,
    signal?: AbortSignal,
  ): Promise<{ listing: SellerListingDetail; minimumExpiryDate: string }> {
    const response = await this.get<SellerListingDetail>(
      `/seller/listings/${id}`,
      undefined,
      signal,
    );

    const meta = (response.meta ?? {}) as { minimum_expiry_date?: string };

    return {
      listing: response.data,
      minimumExpiryDate: meta.minimum_expiry_date ?? '',
    };
  }

  /** `product_id` and a price. Never a drug fact (§9.1). */
  async createListing(input: {
    productId: number;
    priceKobo: number;
    reorderLevel?: number;
  }): Promise<SellerListingRow> {
    const { data } = await this.request<SellerListingRow>('/seller/listings', {
      method: 'POST',
      body: {
        product_id: input.productId,
        price_kobo: input.priceKobo,
        reorder_level: input.reorderLevel,
      },
    });

    return data;
  }

  async updateListing(
    id: number,
    input: { priceKobo?: number; reorderLevel?: number; isActive?: boolean },
  ): Promise<SellerListingRow> {
    const { data } = await this.request<SellerListingRow>(`/seller/listings/${id}`, {
      method: 'PATCH',
      body: {
        price_kobo: input.priceKobo,
        reorder_level: input.reorderLevel,
        is_active: input.isActive,
      },
    });

    return data;
  }

  async addBatch(
    listingId: number,
    input: { batchNo: string; expiryDate: string; quantity: number },
  ): Promise<SellerBatchRow> {
    const { data } = await this.request<SellerBatchRow>(`/seller/listings/${listingId}/batches`, {
      method: 'POST',
      body: {
        batch_no: input.batchNo,
        expiry_date: input.expiryDate,
        quantity: input.quantity,
      },
    });

    return data;
  }

  /** Takes the NEW total, because that is what somebody at a shelf knows. */
  async adjustBatch(batchId: number, quantity: number, reason: string): Promise<SellerBatchRow> {
    const { data } = await this.request<SellerBatchRow>(`/seller/batches/${batchId}`, {
      method: 'PATCH',
      body: { quantity, reason },
    });

    return data;
  }

  // -- seller console (§3.4) -----------------------------------------------

  /**
   * The pharmacy's work queue, already sorted oldest-first by the API.
   *
   * `pharmacyId` is only accepted from an admin acting for a pharmacy whose
   * tablet is broken. A pharmacy owner never sends it and the server would
   * ignore it if they did.
   */
  async sellerOrders(
    pharmacyId?: number,
    signal?: AbortSignal,
  ): Promise<{ orders: SellerOrderRow[]; meta: SellerQueueMeta }> {
    const response = await this.get<SellerOrderRow[]>(
      '/seller/sub-orders',
      pharmacyId ? { pharmacy_id: pharmacyId } : undefined,
      signal,
    );

    return {
      orders: response.data,
      meta: (response.meta ?? {}) as unknown as SellerQueueMeta,
    };
  }

  async sellerOrder(
    reference: string,
    signal?: AbortSignal,
  ): Promise<{ order: SellerOrderDetail; nextAction: string | null }> {
    const response = await this.get<SellerOrderDetail>(
      `/seller/sub-orders/${encodeURIComponent(reference)}`,
      undefined,
      signal,
    );

    return {
      order: response.data,
      nextAction: ((response.meta ?? {}) as { next_action?: string | null }).next_action ?? null,
    };
  }

  async acceptOrder(reference: string): Promise<SellerOrderRow> {
    const { data } = await this.request<SellerOrderRow>(
      `/seller/sub-orders/${encodeURIComponent(reference)}/accept`,
      { method: 'POST' },
    );

    return data;
  }

  /** The seal number is the custody chain in one string, so it is required. */
  async packOrder(reference: string, sealNumber: string): Promise<SellerOrderRow> {
    const { data } = await this.request<SellerOrderRow>(
      `/seller/sub-orders/${encodeURIComponent(reference)}/pack`,
      { method: 'POST', body: { seal_number: sealNumber } },
    );

    return data;
  }

  /**
   * Handing the bag to a rider. This is the tap that fires the goods charge and
   * sends the buyer their delivery code, so it is idempotency-keyed: a
   * pharmacist double-tapping on a bad connection must not dispatch twice.
   */
  async dispatchOrder(
    reference: string,
    input: { riderId?: number; sealNumber?: string },
    idempotencyKey: string,
  ): Promise<{ reference: string; status: string; code_sent_to: string[] }> {
    const { data } = await this.request<{
      reference: string;
      status: string;
      code_sent_to: string[];
    }>(`/seller/sub-orders/${encodeURIComponent(reference)}/dispatch`, {
      method: 'POST',
      body: { rider_id: input.riderId, seal_number: input.sealNumber },
      idempotencyKey,
    });

    return data;
  }

  /**
   * What THIS pharmacy owes, from their own side.
   *
   * Distinct from `sellerDebts()`, which is the admin collections queue across
   * every pharmacy. Same subject, opposite audience: one is a bill, the other
   * is a worklist.
   */
  async sellerDebtStatement(
    signal?: AbortSignal,
  ): Promise<{
    statement: SellerDebtStatement;
    suspendedForDebt: boolean;
    howToPay: {
      bank_name: string | null;
      account_name: string | null;
      account_number: string | null;
      use_reference: string | null;
      support_phone: string | null;
    } | null;
    note: string;
  }> {
    const response = await this.get<SellerDebtStatement>('/seller/debts', undefined, signal);

    const meta = (response.meta ?? {}) as {
      suspended_for_debt?: boolean;
      how_to_pay?: never;
      note?: string;
    };

    return {
      statement: response.data,
      suspendedForDebt: meta.suspended_for_debt ?? false,
      howToPay: meta.how_to_pay ?? null,
      note: meta.note ?? '',
    };
  }

  // -- rider app (§3.1-3.3) ------------------------------------------------

  async riderRuns(signal?: AbortSignal): Promise<{ runs: RiderRunRow[]; meta: RiderRunsMeta }> {
    const response = await this.get<RiderRunRow[]>('/rider/runs', undefined, signal);

    return {
      runs: response.data,
      meta: (response.meta ?? {}) as unknown as RiderRunsMeta,
    };
  }

  async riderRun(
    reference: string,
    signal?: AbortSignal,
  ): Promise<{ run: RiderRunDetail; doorCheckSeconds: number; note: string }> {
    const response = await this.get<RiderRunDetail>(
      `/rider/runs/${encodeURIComponent(reference)}`,
      undefined,
      signal,
    );

    const meta = (response.meta ?? {}) as { door_check_seconds?: number; note?: string };

    return {
      run: response.data,
      doorCheckSeconds: meta.door_check_seconds ?? 180,
      note: meta.note ?? '',
    };
  }

  /**
   * The rider says they are at the door. On a door-capture order this is what
   * fires the charge — and the handover screen stays locked until it clears.
   */
  async riderArrive(
    reference: string,
  ): Promise<{ reference: string; ready_for_handover: boolean; awaiting_payment: boolean }> {
    const { data } = await this.request<{
      reference: string;
      ready_for_handover: boolean;
      awaiting_payment: boolean;
    }>(`/rider/sub-orders/${encodeURIComponent(reference)}/arrive`, { method: 'POST' });

    return data;
  }

  /**
   * The buyer reads out their code and the rider types it. §4.4: this is the
   * binding event of the whole transaction, and the code is single-use.
   */
  async riderHandover(
    reference: string,
    input: { code: string; recipientName?: string },
  ): Promise<{ reference: string; status: string; delivered_at: string }> {
    const { data } = await this.request<{
      reference: string;
      status: string;
      delivered_at: string;
    }>(`/rider/sub-orders/${encodeURIComponent(reference)}/handover`, {
      method: 'POST',
      body: { code: input.code, recipient_name: input.recipientName },
    });

    return data;
  }

  // -- refunds, buyer side (§4.5) -------------------------------------------

  /**
   * What a buyer may still claim for one delivery, and why the rest are shut.
   *
   * The API decides, not the screen: which reasons are open is a money rule,
   * and an old mobile build must not offer one the server will refuse.
   */
  async refundOptions(subOrderReference: string, signal?: AbortSignal): Promise<RefundOptions> {
    const { data } = await this.get<RefundOptions>(
      `/sub-orders/${encodeURIComponent(subOrderReference)}/refund-options`,
      undefined,
      signal,
    );

    return data;
  }

  /**
   * One evidence photo, to the private disk.
   *
   * Multipart, so it bypasses `request()` for the same reason
   * `uploadPharmacyDocument` does. The returned path is opaque and only works
   * on a claim filed by the same buyer.
   */
  async uploadRefundEvidence(file: File | Blob): Promise<RefundEvidenceUpload> {
    const form = new FormData();
    form.append('file', file);

    const token = await this.tokenStore.get();

    const response = await this.doFetch(`${this.baseUrl}/refund-evidence`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: form,
    });

    const payload = await this.parseJson(response);

    if (!response.ok) {
      throw new ApiError(response.status, this.asErrorBody(payload, response.status));
    }

    return (payload as ApiEnvelope<RefundEvidenceUpload>).data;
  }

  /**
   * File the claim.
   *
   * `idempotencyKey` is required. Generate it once when the buyer reaches the
   * final step and reuse it on every retry, so a dropped response is still one
   * claim.
   */
  async fileRefundRequest(
    subOrderReference: string,
    input: {
      reasonCode: RefundReasonOption['key'];
      description?: string;
      media: string[];
      declaration: boolean;
    },
    idempotencyKey: string,
  ): Promise<FiledRefund> {
    const response = await this.request<BuyerRefundClaim>(
      `/sub-orders/${encodeURIComponent(subOrderReference)}/refund-requests`,
      {
        method: 'POST',
        idempotencyKey,
        body: {
          reason_code: input.reasonCode,
          description: input.description,
          media: input.media,
          declaration: input.declaration,
        },
      },
    );

    const meta = (response.meta ?? {}) as { outcome?: RefundOutcome; message?: string };

    return {
      claim: response.data,
      outcome: meta.outcome ?? 'review',
      message: meta.message ?? '',
    };
  }

  /** An evidence photo for a reviewer, fetched with the token — never a public URL. */
  async refundEvidenceBlob(reference: string, index: number): Promise<Blob> {
    const token = await this.tokenStore.get();

    const response = await this.doFetch(
      `${this.baseUrl}/admin/refunds/${encodeURIComponent(reference)}/media/${index}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );

    if (!response.ok) {
      throw new Error('That photo could not be opened.');
    }

    return response.blob();
  }

  // -- catalogue, staff side (§7.2, §9.1) -----------------------------------

  /**
   * The catalogue as staff maintain it.
   *
   * Drug facts are ChiCare's, never a seller's: a pharmacy able to type its own
   * NAFDAC number could type any, and a counterfeit pack would reach a buyer
   * wearing a real registration beside it.
   */
  async adminProducts(
    params: { q?: string; status?: string; sector?: Sector | 'all'; per_page?: number } = {},
    signal?: AbortSignal,
  ): Promise<{ products: AdminProductRow[]; meta: AdminProductMeta }> {
    const response = await this.get<AdminProductRow[]>('/admin/catalogue/products', params, signal);

    return { products: response.data, meta: (response.meta ?? {}) as unknown as AdminProductMeta };
  }

  /**
   * The whole category tree, both sides of the wall.
   *
   * A curator files into either. The public `/categories` endpoint serves one
   * side and falls back to human on anything it does not recognise — that
   * fallback is a safety property, so it gets its own endpoint here rather than
   * an "all" flag that would turn the fallback off.
   */
  async adminCategories(signal?: AbortSignal): Promise<AdminCategoryOption[]> {
    const { data } = await this.get<AdminCategoryOption[]>(
      '/admin/catalogue/categories',
      undefined,
      signal,
    );

    return data;
  }

  /**
   * Add a pack photograph.
   *
   * Multipart by hand: `request()` sets a JSON content type and stringifies
   * the body, which turns a FormData into "{}" and uploads nothing.
   */
  async addProductPhoto(productId: number, file: File | Blob, alt?: string): Promise<ProductPhoto[]> {
    const form = new FormData();
    form.append('file', file);

    if (alt) {
      form.append('alt', alt);
    }

    const token = await this.tokenStore.get();

    const response = await this.doFetch(
      `${this.baseUrl}/admin/catalogue/products/${productId}/photos`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: form,
      },
    );

    const payload = await this.parseJson(response);

    if (!response.ok) {
      throw new ApiError(response.status, this.asErrorBody(payload, response.status));
    }

    return (payload as ApiEnvelope<ProductPhoto[]>).data;
  }

  /** Make it the pack shot — position 0, what a buyer sees first. */
  async makeProductPhotoPrimary(productId: number, photoId: number): Promise<ProductPhoto[]> {
    const { data } = await this.request<ProductPhoto[]>(
      `/admin/catalogue/products/${productId}/photos/${photoId}/primary`,
      { method: 'POST', body: {} },
    );

    return data;
  }

  async removeProductPhoto(productId: number, photoId: number): Promise<ProductPhoto[]> {
    const { data } = await this.request<ProductPhoto[]>(
      `/admin/catalogue/products/${productId}/photos/${photoId}`,
      { method: 'DELETE' },
    );

    return data;
  }

  async adminProduct(id: number, signal?: AbortSignal): Promise<AdminProductDetail> {
    const { data } = await this.get<AdminProductDetail>(`/admin/catalogue/products/${id}`, undefined, signal);

    return data;
  }

  async createProduct(payload: Record<string, unknown>): Promise<AdminProductRow> {
    const { data } = await this.request<AdminProductRow>('/admin/catalogue/products', {
      method: 'POST',
      body: payload,
    });

    return data;
  }

  async updateProduct(id: number, payload: Record<string, unknown>): Promise<AdminProductRow> {
    const { data } = await this.request<AdminProductRow>(`/admin/catalogue/products/${id}`, {
      method: 'PATCH',
      body: payload,
    });

    return data;
  }

  /** Publishing changes what buyers can find, so it carries a written reason. */
  async setProductStatus(id: number, status: 'draft' | 'published', reason: string): Promise<AdminProductRow> {
    const { data } = await this.request<AdminProductRow>(`/admin/catalogue/products/${id}/status`, {
      method: 'POST',
      body: { status, reason },
    });

    return data;
  }

  async deleteProduct(id: number, reason: string): Promise<void> {
    await this.request(`/admin/catalogue/products/${id}`, { method: 'DELETE', body: { reason } });
  }

  /** A second barcode for a redesigned carton — the old one keeps scanning. */
  async addProductIdentifier(
    id: number,
    input: { type: 'gtin' | 'nafdac'; value: string; note?: string },
  ): Promise<AdminProductRow> {
    const { data } = await this.request<AdminProductRow>(`/admin/catalogue/products/${id}/identifiers`, {
      method: 'POST',
      body: input,
    });

    return data;
  }

  async removeProductIdentifier(id: number, identifierId: number): Promise<AdminProductRow> {
    const { data } = await this.request<AdminProductRow>(
      `/admin/catalogue/products/${id}/identifiers/${identifierId}`,
      { method: 'DELETE' },
    );

    return data;
  }

  // -- catalogue submissions ------------------------------------------------

  async catalogueSubmissions(
    status = 'pending',
    signal?: AbortSignal,
  ): Promise<{ submissions: CatalogueSubmissionRow[]; counts: Record<string, number>; pending: number }> {
    const response = await this.get<CatalogueSubmissionRow[]>('/admin/catalogue/submissions', { status }, signal);
    const meta = (response.meta ?? {}) as { counts?: Record<string, number>; pending?: number };

    return { submissions: response.data, counts: meta.counts ?? {}, pending: meta.pending ?? 0 };
  }

  /**
   * One submission, beside everything it might already be.
   *
   * `candidates` is the whole point: approving a fourth "Amoxil 500mg" should
   * mean ignoring three matches on screen, and merging should be the easy path.
   */
  async catalogueSubmission(
    reference: string,
    signal?: AbortSignal,
  ): Promise<{ submission: CatalogueSubmissionDetail; candidates: SubmissionCandidate[]; hasCertainMatch: boolean }> {
    const response = await this.get<CatalogueSubmissionDetail>(
      `/admin/catalogue/submissions/${encodeURIComponent(reference)}`,
      undefined,
      signal,
    );

    const meta = (response.meta ?? {}) as { candidates?: SubmissionCandidate[]; has_certain_match?: boolean };

    return {
      submission: response.data,
      candidates: meta.candidates ?? [],
      hasCertainMatch: Boolean(meta.has_certain_match),
    };
  }

  /** Pass `product_id` to merge into a product that exists; omit it to create one. */
  async approveSubmission(
    reference: string,
    decision: Record<string, unknown>,
  ): Promise<{ submission: CatalogueSubmissionRow; message: string }> {
    const response = await this.request<CatalogueSubmissionRow>(
      `/admin/catalogue/submissions/${encodeURIComponent(reference)}/approve`,
      { method: 'POST', body: decision },
    );

    return { submission: response.data, message: ((response.meta ?? {}) as { message?: string }).message ?? '' };
  }

  async rejectSubmission(reference: string, note: string): Promise<CatalogueSubmissionRow> {
    const { data } = await this.request<CatalogueSubmissionRow>(
      `/admin/catalogue/submissions/${encodeURIComponent(reference)}/reject`,
      { method: 'POST', body: { note } },
    );

    return data;
  }

  /** A photograph of the pack, fetched with the token — never a public URL. */
  async submissionPhotoBlob(reference: string, index: number): Promise<Blob> {
    const token = await this.tokenStore.get();

    const response = await this.doFetch(
      `${this.baseUrl}/admin/catalogue/submissions/${encodeURIComponent(reference)}/photos/${index}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );

    if (!response.ok) {
      throw new Error('That photo could not be opened.');
    }

    return response.blob();
  }

  // -- catalogue, seller side -----------------------------------------------

  /**
   * What a scanned or typed code is.
   *
   * A known barcode goes straight to the product; an unknown one opens a
   * submission with the code already captured.
   */
  async catalogueLookup(code: string, signal?: AbortSignal): Promise<CatalogueLookupResult> {
    const { data } = await this.get<CatalogueLookupResult>('/seller/catalogue/lookup', { code }, signal);

    return data;
  }

  /**
   * Search the whole catalogue, including products nobody stocks yet.
   *
   * The buyer-facing search hides those — an offer ladder with no rungs is a
   * dead end — which would strand the first pharmacy to carry a medicine.
   */
  async sellerCatalogueSearch(query: string, signal?: AbortSignal): Promise<CatalogueSearchRow[]> {
    const { data } = await this.get<CatalogueSearchRow[]>('/seller/catalogue/search', { q: query }, signal);

    return data;
  }

  async sellerSubmissions(signal?: AbortSignal): Promise<SellerSubmissionRow[]> {
    const { data } = await this.get<SellerSubmissionRow[]>('/seller/catalogue/submissions', undefined, signal);

    return data;
  }

  async submitProduct(payload: Record<string, unknown>): Promise<{
    submission: SellerSubmissionRow & { found?: boolean; product?: { id: number; display_name: string } };
    message: string;
  }> {
    const response = await this.request<SellerSubmissionRow>('/seller/catalogue/submissions', {
      method: 'POST',
      body: payload,
    });

    return {
      submission: response.data,
      message: ((response.meta ?? {}) as { message?: string }).message ?? '',
    };
  }

  /** One photograph of the pack, to the private disk. Multipart, so not via `request()`. */
  async uploadCataloguePhoto(file: File | Blob): Promise<{ path: string }> {
    const form = new FormData();
    form.append('file', file);

    const token = await this.tokenStore.get();

    const response = await this.doFetch(`${this.baseUrl}/seller/catalogue/photos`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: form,
    });

    const payload = await this.parseJson(response);

    if (!response.ok) {
      throw new ApiError(response.status, this.asErrorBody(payload, response.status));
    }

    return (payload as ApiEnvelope<{ path: string }>).data;
  }

  /**
   * A whole stock file at once.
   *
   * `dryRun` changes nothing and reports what would happen — the first thing
   * anybody does with a thousand-row file is check it.
   */
  /**
   * The shelves a seller can download, and how many products are on each.
   */
  async catalogueSheetOptions(signal?: AbortSignal): Promise<{
    categories: Array<{ id: number; name: string; sector: string; products: number }>;
    sectors: Array<{ value: string; label: string }>;
    columns: string[];
  }> {
    const response = await this.get<
      Array<{ id: number; name: string; sector: string; products: number }>
    >('/seller/catalogue/template/categories', undefined, signal);

    const meta = (response.meta ?? {}) as {
      sectors?: Array<{ value: string; label: string }>;
      columns?: string[];
    };

    return {
      categories: response.data,
      sectors: meta.sectors ?? [],
      columns: meta.columns ?? [],
    };
  }

  /**
   * Download the catalogue as a spreadsheet.
   *
   * Fetched rather than linked, because the endpoint needs a bearer token and
   * an <a href> cannot carry one. The blob is handed back for the caller to
   * save — the client does not touch the DOM, so this stays usable from the
   * mobile app.
   */
  async catalogueSheet(sector: string, categoryId?: number | null): Promise<Blob> {
    const url = new URL(`${this.baseUrl}/seller/catalogue/template`);
    url.searchParams.set('sector', sector);

    if (categoryId) {
      url.searchParams.set('category_id', String(categoryId));
    }

    const token = await this.tokenStore.get();

    const response = await this.doFetch(url.toString(), {
      headers: {
        Accept: 'text/csv',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      const payload = await this.parseJson(response);

      throw new ApiError(response.status, this.asErrorBody(payload, response.status));
    }

    return response.blob();
  }

  async importListings(
    file: File | Blob,
    dryRun = false,
  ): Promise<{ rows: ImportRow[]; summary: ImportSummary; dryRun: boolean }> {
    const form = new FormData();
    form.append('file', file);
    form.append('dry_run', dryRun ? '1' : '0');

    const token = await this.tokenStore.get();

    const response = await this.doFetch(`${this.baseUrl}/seller/catalogue/import`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: form,
    });

    const payload = await this.parseJson(response);

    if (!response.ok) {
      throw new ApiError(response.status, this.asErrorBody(payload, response.status));
    }

    const envelope = payload as ApiEnvelope<ImportRow[]>;
    const meta = (envelope.meta ?? {}) as { summary?: ImportSummary; dry_run?: boolean };

    return {
      rows: envelope.data,
      summary: meta.summary ?? { total: 0, listed: 0, restocked: 0, needs_review: 0, errors: 0 },
      dryRun: Boolean(meta.dry_run),
    };
  }

  // -- refund review (§4.5, §4.6) ------------------------------------------

  /**
   * The claim queue, already ordered by whichever settlement window shuts
   * first. The API does that ordering, not the client — a second client
   * sorting differently would quietly cost money.
   */
  async refundClaims(
    status = 'open',
    signal?: AbortSignal,
  ): Promise<{ claims: RefundClaimRow[]; meta: RefundQueueMeta }> {
    const response = await this.get<RefundClaimRow[]>('/admin/refunds', { status }, signal);

    return {
      claims: response.data,
      meta: (response.meta ?? {}) as unknown as RefundQueueMeta,
    };
  }

  async refundClaim(
    reference: string,
    signal?: AbortSignal,
  ): Promise<{ claim: RefundClaimDetail; context: RefundClaimContext }> {
    const response = await this.get<RefundClaimDetail>(
      `/admin/refunds/${encodeURIComponent(reference)}`,
      undefined,
      signal,
    );

    return {
      claim: response.data,
      context: (response.meta ?? {}) as unknown as RefundClaimContext,
    };
  }

  /**
   * §8: approving SENDS the money. It is not a status change — Paystack is
   * called inside the same operation, so an approval that appears to have
   * worked really has moved money. Idempotency-keyed because a double tap on a
   * slow connection must never be a second refund.
   */
  async approveRefund(
    reference: string,
    input: { note?: string; amountKobo?: number },
    idempotencyKey: string,
  ): Promise<RefundClaimRow> {
    const { data } = await this.request<RefundClaimRow>(
      `/admin/refunds/${encodeURIComponent(reference)}/approve`,
      {
        method: 'POST',
        body: { note: input.note, amount_kobo: input.amountKobo },
        idempotencyKey,
      },
    );

    return data;
  }

  /** The note is required: the buyer reads it, and "declined" alone is not an answer. */
  async declineRefund(reference: string, note: string): Promise<RefundClaimRow> {
    const { data } = await this.request<RefundClaimRow>(
      `/admin/refunds/${encodeURIComponent(reference)}/decline`,
      { method: 'POST', body: { note } },
    );

    return data;
  }

  // -- collections (§4.11) -------------------------------------------------

  async sellerDebts(
    status = 'open',
    signal?: AbortSignal,
  ): Promise<{ debts: SellerDebtRow[]; meta: { outstanding_display: string; count: number } }> {
    const response = await this.get<SellerDebtRow[]>('/admin/seller-debts', { status }, signal);

    return {
      debts: response.data,
      meta: (response.meta ?? {}) as unknown as { outstanding_display: string; count: number },
    };
  }

  async sellerDebt(
    pharmacyId: number,
    signal?: AbortSignal,
  ): Promise<{ statement: SellerDebtStatement; pharmacy: SellerDebtContact }> {
    const response = await this.get<SellerDebtStatement>(
      `/admin/seller-debts/${pharmacyId}`,
      undefined,
      signal,
    );

    return {
      statement: response.data,
      pharmacy: ((response.meta ?? {}) as unknown as { pharmacy: SellerDebtContact }).pharmacy,
    };
  }

  /**
   * Log a bank transfer that has landed.
   *
   * The reference is what makes this checkable against a statement later —
   * without it an admin is only asserting that money arrived. The API refuses
   * the call rather than trusting the console to ask.
   */
  async recordDebtPayment(
    pharmacyId: number,
    input: { amountKobo: number; reference: string; note?: string },
    idempotencyKey: string,
  ): Promise<{ statement: SellerDebtStatement; result: DebtPaymentResult }> {
    const response = await this.request<SellerDebtStatement>(
      `/admin/seller-debts/${pharmacyId}/payments`,
      {
        method: 'POST',
        body: { amount_kobo: input.amountKobo, reference: input.reference, note: input.note },
        idempotencyKey,
      },
    );

    return {
      statement: response.data,
      result: (response.meta ?? {}) as unknown as DebtPaymentResult,
    };
  }

  async adminSettingsHistory(signal?: AbortSignal): Promise<SettingsHistoryEntry[]> {
    const { data } = await this.get<SettingsHistoryEntry[]>('/admin/settings/history', undefined, signal);

    return data;
  }

  // -- admin: shelf life (§9.3) ---------------------------------------------

  async shelfLife(signal?: AbortSignal): Promise<ShelfLifeOverview> {
    const { data } = await this.get<ShelfLifeOverview>('/admin/shelf-life', undefined, signal);

    return data;
  }

  /** Read-only. Asking "what would happen" must never cause it to happen. */
  async previewShelfLife(months: number, signal?: AbortSignal): Promise<ShelfLifePreview> {
    const { data } = await this.request<ShelfLifePreview>('/admin/shelf-life/preview', {
      method: 'POST',
      body: { months },
      signal,
    });

    return data;
  }

  async setCategoryShelfLife(
    categoryId: number,
    months: number | null,
    reason: string,
  ): Promise<void> {
    await this.request(`/admin/shelf-life/categories/${categoryId}`, {
      method: 'PATCH',
      body: { min_shelf_life_months: months, reason },
    });
  }

  async runShelfLifePass(dryRun = true, reason?: string): Promise<{ output: string; dry_run: boolean }> {
    const { data } = await this.request<{ output: string; dry_run: boolean }>('/admin/shelf-life/run', {
      method: 'POST',
      body: { dry_run: dryRun, reason },
    });

    return data;
  }

  async restoreListing(listingId: number, reason: string): Promise<void> {
    await this.request(`/admin/shelf-life/listings/${listingId}/restore`, {
      method: 'POST',
      body: { reason },
    });
  }

  // -- the blog, for readers -------------------------------------------------

  /**
   * Published articles for one shelf.
   *
   * `sector` is not optional in practice: the API defaults to HUMAN when it is
   * missing, which is the safe direction, but a vet screen that forgets it
   * silently shows the human shelf.
   */
  async blog(
    params: {
      sector?: string;
      category?: string | null;
      q?: string | null;
      page?: number;
    } = {},
    signal?: AbortSignal,
  ): Promise<BlogIndex> {
    const { data, meta } = await this.get<BlogCard[]>(
      '/blog',
      {
        sector: params.sector,
        category: params.category || undefined,
        q: params.q || undefined,
        page: params.page && params.page > 1 ? params.page : undefined,
      },
      signal,
    );

    const info = (meta ?? {}) as Partial<BlogIndex>;

    return {
      posts: data,
      page: info.page ?? 1,
      pages: info.pages ?? 1,
      total: info.total ?? data.length,
      categories: info.categories ?? [],
    };
  }

  async blogArticle(slug: string, sector?: string, signal?: AbortSignal): Promise<BlogArticle> {
    const { data } = await this.get<BlogArticle>(`/blog/${slug}`, { sector }, signal);

    return data;
  }

  // -- admin: the blog -------------------------------------------------------

  async adminPosts(
    params: { status?: string; category?: string; q?: string } = {},
    signal?: AbortSignal,
  ): Promise<BlogDesk> {
    const { data, meta } = await this.get<BlogPostSummary[]>(
      '/admin/posts',
      {
        status: params.status || undefined,
        category: params.category || undefined,
        q: params.q || undefined,
      },
      signal,
    );

    const info = (meta ?? {}) as { can_publish?: boolean; categories?: string[] };

    return {
      posts: data,
      // Defaults to FALSE, so a payload that somehow arrives without the flag
      // offers a draft rather than a Publish button that answers 403.
      can_publish: info.can_publish === true,
      categories: info.categories ?? [],
    };
  }

  async adminPost(slug: string, signal?: AbortSignal): Promise<BlogPostDetail> {
    const { data } = await this.get<BlogPostDetail>(`/admin/posts/${slug}`, undefined, signal);

    return data;
  }

  async saveAdminPost(slug: string | null, post: BlogPostInput): Promise<BlogPostSummary> {
    const { data } = await this.request<BlogPostSummary>(
      slug ? `/admin/posts/${slug}` : '/admin/posts',
      { method: slug ? 'PATCH' : 'POST', body: post },
    );

    return data;
  }

  async deleteAdminPost(slug: string, reason?: string): Promise<void> {
    await this.request(`/admin/posts/${slug}`, { method: 'DELETE', body: { reason } });
  }

  async restorePostRevision(slug: string, version: number, reason?: string): Promise<void> {
    await this.request(`/admin/posts/${slug}/revisions/${version}/restore`, {
      method: 'POST',
      body: { reason },
    });
  }

  // -- admin: content pages -------------------------------------------------

  async adminPages(signal?: AbortSignal): Promise<ContentPageSummary[]> {
    const { data } = await this.get<ContentPageSummary[]>('/admin/pages', undefined, signal);

    return data;
  }

  async adminPage(slug: string, signal?: AbortSignal): Promise<ContentPageDetail> {
    const { data } = await this.get<ContentPageDetail>(`/admin/pages/${slug}`, undefined, signal);

    return data;
  }

  async saveAdminPage(
    slug: string | null,
    page: Partial<ContentPageDetail> & { change_note?: string },
  ): Promise<ContentPageSummary> {
    const { data } = await this.request<ContentPageSummary>(
      slug ? `/admin/pages/${slug}` : '/admin/pages',
      { method: slug ? 'PATCH' : 'POST', body: page },
    );

    return data;
  }

  async deleteAdminPage(slug: string, reason?: string): Promise<void> {
    await this.request(`/admin/pages/${slug}`, { method: 'DELETE', body: { reason } });
  }

  async restorePageRevision(slug: string, version: number, reason: string): Promise<void> {
    await this.request(`/admin/pages/${slug}/revisions/${version}/restore`, {
      method: 'POST',
      body: { reason },
    });
  }

  // -- admin: media ---------------------------------------------------------

  async adminMedia(collection?: string, signal?: AbortSignal): Promise<MediaAsset[]> {
    const { data } = await this.get<MediaAsset[]>('/admin/media', { collection }, signal);

    return data;
  }

  /**
   * Multipart, so it bypasses the JSON body path. FormData exists in React
   * Native too, which is why this still belongs in the shared client.
   */
  async uploadMedia(file: File | Blob, collection: string, alt?: string): Promise<MediaAsset> {
    const form = new FormData();
    form.append('file', file);
    form.append('collection', collection);
    if (alt) form.append('alt', alt);

    const token = await this.tokenStore.get();

    const response = await this.doFetch(`${this.baseUrl}/admin/media`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: form,
    });

    const payload = await this.parseJson(response);

    if (!response.ok) {
      throw new ApiError(response.status, this.asErrorBody(payload, response.status));
    }

    return (payload as ApiEnvelope<MediaAsset>).data;
  }

  async deleteMedia(id: number): Promise<void> {
    await this.request(`/admin/media/${id}`, { method: 'DELETE' });
  }

  // -- system --------------------------------------------------------------

  async config(signal?: AbortSignal): Promise<SystemConfig> {
    const { data } = await this.get<SystemConfig>('/system/config', undefined, signal);

    return data;
  }
}

/**
 * §8 / §9.14. One key per logical operation, not per attempt: the whole point
 * is that a retry of the SAME order carries the SAME key.
 */
export function newIdempotencyKey(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  return `idem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
